import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import {
  schoolService,
  getLanguages,
  getSelectedLanguages,
  updateSelectedLanguages,
  getMenuTypes,
  deleteMenuType,
  getMenuTypeCourses,
  getAvailableCoursesForMenuType,
  getMenuTypeUsage,
  getCourseUsage,
  menuTypeService,
  courseService,
  getSchoolUsers,
  checkUserExists
} from '../utils/api';
import { useReorderMenuTypeCourse, useMenuTypeCourses, useAvailableCoursesForMenuType, useAddCourseToMenuType, useRemoveCourseFromMenuType } from '../hooks/useMenuTypeCourses';
import { useCourses, useUpdateCourse, useCreateCourse, useDeleteCourse } from '../hooks/useCourses';
import { useSchoolUsers, useInviteUserToSchool, useUpdateUserRoles, useRemoveUserFromSchool, useUserSchools } from '../hooks/useSchool';
import { useSchoolSettings, useUpdateSchoolSettings } from '../hooks/useSchoolSettings';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import FileUploader from '../components/FileUploader';
import ColorPicker from '../components/ColorPicker';
import { useUser } from '../contexts/UserContext';
import { interpolate } from '../utils/interpolation';
import { validateSubdomain } from '../utils/subdomainValidator.js';
import SimpleEditor from '../components/SimpleEditor';
import { useSchool } from '../contexts/SchoolContext';
import languageService from '../utils/languageService';
import { getPublicUrl } from '../utils/nhost';
import ConfirmationModal from '../components/ConfirmationModal';
import { updateBranding } from '../redux/slices/brandingSlice';
import { languageSlice } from '../redux/slices/languageSlice.js';
import { useNutrients, useCreateNutrient, useDeleteNutrient } from '../hooks/useNutrients';
import { useMutation } from '@tanstack/react-query';
import { getAccessToken } from '../utils/auth';
import NutrientsTab from '../components/Settings/NutrientsTab';
import TagTab from '../components/Settings/TagTab';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';

// Add custom styles to override any browser defaults
const noOutlineStyles = `
  .no-outline-tabs {
    -webkit-tap-highlight-color: transparent;
  }
  
  .no-outline-tabs button:focus,
  .no-outline-tabs button:active,
  .no-outline-tabs button:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useUser();
  const { school } = useSchool();
  const queryClient = useQueryClient(); // Add this line
  const dispatch = useDispatch();
  const { currentLanguage } = useSelector(state => state.language);
  // Get current school ID from Redux store (set by SchoolContext)
  // FIXED: Access from language slice instead of non-existent school slice 
  const currentSchoolId = useSelector(state => state.language?.currentSchoolId || null);
  
  // Initialize the reorder mutation hook
  const reorderMenuTypeCourseQuery = useReorderMenuTypeCourse();
  
  // Initialize the add course mutation hook
  const addCourseToMenuTypeQuery = useAddCourseToMenuType();
  
  // Initialize the remove course mutation hook
  const removeCourseFromMenuTypeQuery = useRemoveCourseFromMenuType();
  
  // Initialize React Query hooks for course management
  // CRITICAL FIX: Use correct priority order - Redux store (currentSchoolId) should be first
  const schoolId = currentSchoolId || school?.id || user?.current_school?.id;
  console.log('School ID for React Query hooks (fixed priority):', schoolId, {
    currentSchoolId,
    schoolId: school?.id,
    userCurrentSchoolId: user?.current_school?.id
  });

  // School settings hook
  const { 
    data: schoolSettingsData, 
    isLoading: isSchoolSettingsLoading,
    error: schoolSettingsError
  } = useSchoolSettings(schoolId);
  
  // Use fresh school data for permissions check
  const { data: userSchools = [] } = useUserSchools();

  // School settings mutation hook
  const updateSchoolSettingsMutation = useUpdateSchoolSettings();

  // Query hook will automatically re-run when dependencies change
  const { data: coursesData = [], isLoading: isCoursesLoading, isError: isCoursesError, error: coursesError } = useCourses(schoolId);

  // If there's an error, we can log it once, not during every render
  useEffect(() => {
    if (isCoursesError && coursesError) {
      console.error('Error fetching courses:', coursesError);
    }
  }, [isCoursesError, coursesError]);

  const createCourseQuery = useCreateCourse();
  const updateCourseQuery = useUpdateCourse();
  const deleteCourseQuery = useDeleteCourse();
  
  // Initialize React Query hooks for user management
  const { 
    data: usersData = [], 
    isLoading: isUsersLoading, 
    isError: isUsersError 
  } = useSchoolUsers(schoolId);
  
  // Initialize the mutation hooks for user operations
  const inviteUserMutation = useInviteUserToSchool(schoolId);
  const updateUserRolesMutation = useUpdateUserRoles(schoolId);
  const removeUserMutation = useRemoveUserFromSchool(schoolId);
  
  // Initialize React Query hooks for nutrients
  const { 
    data: nutrientsData = [], 
    isLoading: isNutrientsLoading, 
    isError: isNutrientsError 
  } = useNutrients(schoolId);
  
  // Initialize mutation hooks for nutrients
  const createNutrientMutation = useCreateNutrient();
  const updateNutrientMutation = useMutation({
    mutationFn: async ({ nutrientId, nutrient, schoolId }) => {
      const response = await fetch(`/api/schools/${schoolId}/nutrients/${nutrientId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify(nutrient)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update nutrient');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['nutrients', getSchoolId()]);
    }
  });
  const deleteNutrientMutation = useDeleteNutrient();
  
  // Add React Query hook to check if menus exist for the school
  const { 
    data: menusData = [], 
    isLoading: isMenusLoading
  } = useQuery({
    queryKey: ['menus', schoolId],
    queryFn: async () => {
      if (!schoolId) return [];
      const response = await fetch(`/api/admin/menus?schoolId=${schoolId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch menus');
      }
      return response.json();
    },
    enabled: !!schoolId
  });
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [menuTypes, setMenuTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState({
    name: '',
    subdomain: '',
    homepageUrl: '',
    primaryBgColor: '#4A90E2',
    primaryTextColor: '#FFFFFF',
    secondaryBgColor: '#3498DB',
    secondaryTextColor: '#FFFFFF',
    defaultLanguageId: null,
    defaultMenuTypeId: null,
    logoPreview: '',
    logoServerPath: null,
    logo: null,
    customContent: {},
    disclaimer_text: {},
    include_custom_content_in_image: true,
    show_nutrients: false,
    // Add new property to track temporary logo uploads
    tempLogoPath: null,
    // Add month navigation settings
    enable_public_month_navigation: false,
    months_ahead_limit: 0,
    months_back_limit: 0,
    days_after_month_start_for_back_navigation: 7,
    days_before_month_end_for_forward_navigation: 7,
    monthly_public_display: true, // Add this line for the new weekly/monthly display toggle
    show_tags: false, // Add show_tags field here
  });
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);
  const [editModalType, setEditModalType] = useState('');
  const [editModalItem, setEditModalItem] = useState(null);
  const [currentTranslations, setCurrentTranslations] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editError, setEditError] = useState(null);
  const [menuTypeVisibility, setMenuTypeVisibility] = useState(true);
  const [menuTypeShowNutrients, setMenuTypeShowNutrients] = useState(true);
  const [menuTypeShowTags, setMenuTypeShowTags] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalType, setDeleteModalType] = useState('');
  const [deleteModalItem, setDeleteModalItem] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [usageCount, setUsageCount] = useState(0);
  const [expandedMenuType, setExpandedMenuType] = useState(null);
  const [selectedAvailableCourse, setSelectedAvailableCourse] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newMenuTypeIsVisible, setNewMenuTypeIsVisible] = useState(true);
  const [newMenuTypeShowNutrients, setNewMenuTypeShowNutrients] = useState(true);
  const [newMenuTypeShowTags, setNewMenuTypeShowTags] = useState(true);
  const [initialSubdomain, setInitialSubdomain] = useState('');
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownInfo, setCooldownInfo] = useState(null);
  const subdomainTimeoutRef = useRef(null);

  // Add references for menu type items
  const menuTypeRefs = useRef({});

  // Add this at the beginning of the component, near other state declarations
  const initializedRef = useRef(false);
  const errorShownRef = useRef(false);

  // Add a loading state for school info submission
  const [isSubmittingSchoolInfo, setIsSubmittingSchoolInfo] = useState(false);

  // Define a NOOP function for setIsSettingsChanged that we're removing
  const setIsSettingsChanged = () => {
    // This is a no-op function since we're removing the state
    // All calls to this function remain but don't do anything
  };

  // User management state
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState(['Viewer']);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [userPermissions, setUserPermissions] = useState({
    isAdmin: false,
    isSuperadmin: false,
    hasAdminPrivileges: false
  });
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);

  // Add a loading state for menu types at top of component with other state variables
  const [isMenuTypesLoading, setIsMenuTypesLoading] = useState(true);

  // Helper function to get the correct school ID
  const getSchoolId = useCallback(() => {
    // First try to get the school ID from the Redux store (set by SchoolContext from subdomain)
    if (currentSchoolId) {
      console.log('Using school ID from subdomain context:', currentSchoolId);
      return currentSchoolId;
    }
    
    // Then try from SchoolContext's school object
    if (school?.id) {
      console.log('Using school ID from school context:', school.id);
      return school.id;
    }
    
    // Fall back to user's current school
    if (user?.current_school?.id) {
      console.log('Using school ID from user context:', user.current_school.id);
      return user.current_school.id;
    }
    
    console.warn('No school ID available');
    return null;
  }, [currentSchoolId, school, user]);
  
  // Use React Query to fetch menu type courses when expandedMenuType changes
  const { 
    data: menuTypeCourses = [], 
    isLoading: isLoadingMenuTypeCourses 
  } = useMenuTypeCourses(expandedMenuType, {
    enabled: !!expandedMenuType,
  });
  
  // Use React Query to fetch available courses for the menu type
  const { 
    data: availableCourses = [], 
    isLoading: isLoadingAvailableCourses 
  } = useAvailableCoursesForMenuType(expandedMenuType, getSchoolId(), {
    enabled: !!expandedMenuType,
  });
  
  // Combine loading states for UI
  const isLoadingCourses = isLoadingMenuTypeCourses || isLoadingAvailableCourses;

  // Function to fetch users for the current school
  const fetchUsers = useCallback(async () => {
    try {
      // Get school ID from SchoolContext or fallback to user context
    // CRITICAL FIX: Use correct priority order - Redux store (currentSchoolId) should be first
    const schoolId = currentSchoolId || school?.id || user?.current_school?.id;
      
      if (!schoolId) return;
      
      console.log('[DEBUG] Fetching users for school ID:', schoolId);
      console.log('[DEBUG] Current user object:', user);
      console.log('[DEBUG] User schools:', user?.schools);
      
      if (user?.schools) {
        // Find the current school in user's schools array
        const currentSchool = user.schools.find(s => s.id === schoolId);
        console.log('[DEBUG] Current school from schools array:', currentSchool);
        
        if (currentSchool) {
          // Check user roles for this school
          const userRoles = currentSchool.roles || [];
          console.log('[DEBUG] Roles for current school:', userRoles);
          const isSuperadmin = userRoles.includes('Superadmin');
          console.log('[DEBUG] Is superadmin?', isSuperadmin);
        }
      }

      const usersData = await getSchoolUsers(schoolId);
      console.log('[DEBUG] Fetched users:', usersData);
      setUsers(usersData);
      setLoadingUsers(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error(t('settings.errorFetchingUsers'));
      setLoadingUsers(false);
    }
  }, [school, user, t]);

  // Function to handle inviting a user to the school
  const handleInviteUser = async () => {
    if (!userEmail.trim()) {
      setInviteError(t('settings.emailRequired'));
      return;
    }

    if (selectedRoles.length === 0) {
      setInviteError(t('settings.roleRequired'));
      return;
    }

    try {
      setInviteLoading(true);
      setInviteError('');
      
      // First check if the user exists
      const checkResult = await checkUserExists(userEmail);
      
      if (!checkResult.exists) {
        setInviteError(t('settings.userNotExist'));
        setInviteLoading(false);
        return;
      }
      
      // If user exists, invite them to the school using mutation
      // The schoolId is already provided in the hook initialization
      await inviteUserMutation.mutateAsync({
        email: userEmail,
        roles: selectedRoles
      });
      
      // Close the modal and reset form
      setInviteModalOpen(false);
      setUserEmail('');
      setSelectedRoles([]);
      toast.success(t('settings.userInvited'));
    } catch (error) {
      console.error('Error inviting user:', error);
      setInviteError(error.message || t('settings.errorInvitingUser'));
    } finally {
      setInviteLoading(false);
    }
  };

  // Function to handle updating user roles
  const handleUpdateUserRoles = async () => {
    if (!selectedUser) return;
    
    if (selectedRoles.length === 0) {
      toast.error(t('settings.roleRequired'));
      return;
    }

    try {
      // Use the React Query mutation
      // The schoolId is already provided in the hook initialization
      await updateUserRolesMutation.mutateAsync({
        userId: selectedUser.id,
        roles: selectedRoles
      });
      
      // Close the modal and reset form
      setEditUserModalOpen(false);
      setSelectedUser(null);
      setSelectedRoles([]);
      toast.success(t('settings.userRolesUpdated'));
    } catch (error) {
      console.error('Error updating user roles:', error);
      toast.error(error.message || t('settings.errorUpdatingRoles'));
    }
  };

  // Function to handle removing a user from the school
  const handleRemoveUser = async (userId, hasAdminRole, userEmail) => {
    // Check if current user is superadmin or admin for the current school
    // IMPORTANT FIX: Always use school ID from context, not user.current_school
    // CRITICAL FIX: Use correct priority order - Redux store (currentSchoolId) should be first
    const schoolIdForPermissionCheck = currentSchoolId || school?.id || user?.current_school?.id;
    const userRolesForCurrentSchool = user.schools
      .find(school => school.id === schoolIdForPermissionCheck)?.roles || [];
    const isSuperadmin = userRolesForCurrentSchool.includes('Superadmin');
    const isAdmin = userRolesForCurrentSchool.includes('Admin');
    
    // Apply the same permission rules as for editing:
    // 1. Superadmins can remove any user
    // 2. Admins can only remove non-admin users
    if (!isSuperadmin && hasAdminRole) {
      toast.error(t('settings.cannotRemoveAdmin'));
      return;
    }

    // Set the user to delete and open the confirmation modal
    setUserToDelete({ id: userId, email: userEmail });
    setDeleteUserModalOpen(true);
  };

  // Function to confirm and execute user removal
  const confirmRemoveUser = async () => {
    if (!userToDelete) return;
    
    try {
      // Use the React Query mutation for removing users - pass just the userId
      // The schoolId is already provided in the hook initialization
      await removeUserMutation.mutateAsync(userToDelete.id);
      
      toast.success(t('settings.userRemoved'));
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error(error.message || t('settings.errorRemovingUser'));
    } finally {
      // Close the modal and reset state
      setDeleteUserModalOpen(false);
      setUserToDelete(null);
    }
  };

  // Function to cancel user removal
  const cancelRemoveUser = () => {
    setDeleteUserModalOpen(false);
    setUserToDelete(null);
  };

  // Function to open invite user modal
  const openInviteModal = () => {
    setUserEmail('');
    setSelectedRoles([]);
    setInviteError('');
    setInviteModalOpen(true);
  };

  // Function to open edit user modal
  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setSelectedRoles(user.roles || []);
    setEditUserModalOpen(true);
  };

    const fetchLanguages = async () => {
      try {
        const allLanguages = await getLanguages();
        setLanguages(allLanguages);
      } catch (error) {
        console.error('Error fetching languages:', error);
        toast.error(t('settings.errorFetchingLanguages'));
      }
    };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    // Create state flags to track async operations
    let isMounted = true;
    
    const fetchSelectedLanguages = async () => {
      try {
        // Get school ID from SchoolContext or fallback to user context
        const schoolId = getSchoolId();
        
        if (schoolId) {
          const data = await getSelectedLanguages(schoolId);
          // Only update state if component is still mounted
          if (!isMounted) return;
          
          // Check if data exists and is not null
          if (data) {
            setSelectedLanguages(data);
            
            // Make sure we're dispatching a valid action with the data payload
            // Only dispatch if data is an array
            if (Array.isArray(data)) {
              dispatch({ type: 'language/setSelectedLanguages', payload: data });
            } else {
              console.warn('Selected languages data is not an array:', data);
              dispatch({ type: 'language/setSelectedLanguages', payload: [] }); // Dispatch empty array as fallback
            }
          } else {
            dispatch({ type: 'language/setSelectedLanguages', payload: [] }); // Initialize with empty array
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching selected languages:', error);
          toast.error(t('settings.errorFetchingSelectedLanguages'));
          dispatch({ type: 'language/setSelectedLanguages', payload: [] }); // Dispatch empty array on error
        }
      }
    };

    // Update the fetchMenuTypes function to track loading state
    const fetchMenuTypes = async () => {
      try {
        setIsMenuTypesLoading(true);
        const schoolId = getSchoolId();
        const languageId = currentLanguage || 'en';
        
        // Use the schoolId as the first parameter
        const data = await getMenuTypes(schoolId, languageId);
        
        if (isMounted) {
          setMenuTypes(data);
          setIsMenuTypesLoading(false);
        }
      } catch (error) {
        console.error('Error fetching menu types:', error);
        if (isMounted) {
          toast.error(t('settings.errorFetchingMenuTypes'));
          setIsMenuTypesLoading(false);
        }
      }
    };

    // No need to fetch courses here - they're fetched by the useCourses hook

    // Fetch languages early since they don't depend on user
    // Ensure fetchLanguages is included in dependency array if used here
    fetchLanguages(); // Linter warning: fetchLanguages dependency missing
    
    // Get school ID from SchoolContext or fallback to user context
    const schoolId = school?.id || user?.current_school?.id;
    
    if (schoolId) {
      fetchSelectedLanguages();
      // fetchMenuTypes(); // REMOVED
      // No need to fetch courses here
    }
    
    // Cleanup function to handle component unmounting
    return () => {
      isMounted = false;
    };
    // Add fetchLanguages to the dependency array
  }, [user, school, currentLanguage, t, dispatch, getSchoolId, fetchLanguages]);
  
  // When the current tab changes, refetch data for that tab based on the current language
  useEffect(() => {
    // Get school ID from SchoolContext or fallback to user context
    // CRITICAL FIX: Use correct priority order - Redux store (currentSchoolId) should be first
    const schoolId = currentSchoolId || school?.id || user?.current_school?.id;
    
    // Only fetch data if user is logged in with a school context
    if (!schoolId) return;
    
    // Fetch data for the active tab
    if (activeTab === 'menuTypes') {
      const fetchMenuTypes = async () => {
        try {
          const data = await getMenuTypes(schoolId, currentLanguage);
          setMenuTypes(data);
        } catch (error) {
          console.error('Error fetching menu types:', error);
          toast.error(t('settings.errorFetchingMenuTypes'));
        }
      };
      fetchMenuTypes();
    }
    // No need to fetch courses when tab changes - React Query will handle this
  }, [activeTab, currentLanguage, user, school, t]);

  // Effect to reload menu type courses when language changes
  // This is no longer needed since React Query will automatically refetch when dependencies change
  // The useMenuTypeCourses hook has language as part of its query key
  
  // Add a debug effect to monitor user and school context
  useEffect(() => {
    // CRITICAL FIX: Use correct priority order - Redux store (currentSchoolId) should be first
    const schoolId = currentSchoolId || school?.id || user?.current_school?.id;
    console.log('User or school context changed in SettingsPage:', schoolId);
    console.log('SchoolContext school:', school);
    console.log('UserContext school:', user?.current_school);
  }, [user, school]);

  // Helper function to get the best available translation for an item (course or menu type)
  const getBestTranslation = (item) => {
    if (!item || !item.translations) return { text: item?.name || '', isFallback: false };
    
    // First try to find translation in current language
    const currentTrans = item.translations.find(t => t.code === currentLanguage && t.name);
    
    if (currentTrans && currentTrans.name) {
      return { text: currentTrans.name, isFallback: false };
    }
    
    // If no translation in current language, find first non-empty translation
    const anyTrans = item.translations.find(t => t.name);
    
    if (anyTrans && anyTrans.name) {
      return { text: anyTrans.name, isFallback: true, language: anyTrans.code };
    }
    
    // Fallback to the item name field as last resort
    return { text: item.name || '', isFallback: true };
  };

  // Format date based on user locale but always in DD/MM/YYYY format
  const formatDateDDMMYYYY = (date) => {
    // Get the current language from i18n
    const currentLang = i18n.language;
    
    // Format options for UK style date (DD/MM/YYYY)
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    };
    
    // For English, force UK date format with explicit formatting
    if (currentLang === 'en') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // For other languages, use the locale-aware formatting
    return date.toLocaleDateString(currentLang, options);
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    // To be implemented with actual API call
    toast.success(t('settings.userSaved'));
  };

  const handleMenuTypeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the school's default language
      const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code || 'en';
      console.log('Default language code:', defaultLangCode);
      
      // Check if the default language input is filled
      const defaultLangInput = e.target.elements[`translation-${defaultLangCode}`];
      const defaultLangValue = defaultLangInput ? defaultLangInput.value.trim() : '';
      console.log('Default language input value:', defaultLangValue);
      
      if (!defaultLangValue) {
        // Show error if default language translation is missing
        toast.error(t('settings.defaultLanguageRequired', { language: t(`languages.${defaultLangCode}`) }));
        return;
      }

      // Collect translations from form - only include non-empty translations
      const translations = {};
      
      // Add translations for all languages with non-empty values
      selectedLanguages.forEach(lang => {
        const translationInput = e.target.elements[`translation-${lang.code}`];
        if (translationInput && translationInput.value.trim()) {
          translations[lang.code] = translationInput.value.trim();
        }
        // Skip empty translations entirely - don't add them to the object
      });
      
      console.log('Collected translations:', translations);
      
      // Call API with new parameter ordering (schoolId, translations, is_visible)
      const schoolId = getSchoolId();
      console.log('Calling menuTypeService.createMenuType with:', 
        { schoolId, translations, is_visible: newMenuTypeIsVisible, show_nutrients: newMenuTypeShowNutrients, show_tags: newMenuTypeShowTags });
      
      // Create an optimistic menu type entry
      const tempId = `temp-${Date.now()}`;
      const optimisticMenuType = {
        id: tempId,
        name: translations[defaultLangCode],
        school_id: schoolId,
        is_visible: newMenuTypeIsVisible,
        show_nutrients: newMenuTypeShowNutrients,
        show_tags: newMenuTypeShowTags,
        created_at: new Date().toISOString(),
        translations: Object.entries(translations).map(([code, text]) => ({
          code,
          name: text
        }))
      };
      
      // Optimistically update the UI
      setMenuTypes(prevMenuTypes => [...prevMenuTypes, optimisticMenuType]);
      
      // Then perform the actual API call
      const result = await menuTypeService.createMenuType(
        schoolId, 
        translations, 
        newMenuTypeIsVisible,
        newMenuTypeShowNutrients,
        newMenuTypeShowTags
      );
      
      console.log('Menu type created successfully:', result);
      
      // Get the real ID from the API response
      const newMenuTypeId = result.id;
      
      // Check if this is the first menu type and update schoolInfo if needed
      if (menuTypes.length === 0) {
        console.log('First menu type created, updating schoolInfo.defaultMenuTypeId to:', newMenuTypeId);
        setSchoolInfo(prevSchoolInfo => ({
          ...prevSchoolInfo,
          defaultMenuTypeId: newMenuTypeId
        }));
      }
      
      // Replace the optimistic entry with the real one from the API
      setMenuTypes(prevMenuTypes => prevMenuTypes.map(mt => 
        mt.id === tempId ? {
          ...mt,
          id: newMenuTypeId
        } : mt
      ));
      
      // Reset form
      selectedLanguages.forEach(lang => {
        const translationInput = e.target.elements[`translation-${lang.code}`];
        if (translationInput) {
          translationInput.value = '';
        }
      });
      
      // Reset settings to default values
      setNewMenuTypeIsVisible(true);
      setNewMenuTypeShowNutrients(true);
      setNewMenuTypeShowTags(true);
      
      // Access the global query client
      const queryClient = window.__REACT_QUERY_GLOBAL_CLIENT__;
      if (queryClient) {
        console.log(`[DEBUG-MenuTypes] Invalidating caches after creating menu type at ${new Date().toISOString()}`);
        
        // Invalidate all necessary caches
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey[0];
            return queryKey === 'availableMenuTypes' || queryKey === 'publicMenu';
          }
        });
        
        // Also invalidate tag and nutrient queues
        queryClient.invalidateQueries({ queryKey: ['dishesNeedingTags'] });
        queryClient.invalidateQueries({ queryKey: ['nutrientQueue'] });
        
        // Specifically invalidate the availableCoursesForMenuType cache
        queryClient.invalidateQueries({
          queryKey: ['availableCoursesForMenuType']
        });
        
        // Prefetch available courses for the new menu type
        const language = i18n.language || 'en';
        queryClient.prefetchQuery({
          queryKey: ['availableCoursesForMenuType', newMenuTypeId, schoolId, language],
          queryFn: async () => {
            const timestamp = Date.now();
            return getAvailableCoursesForMenuType(newMenuTypeId, language, timestamp);
          }
        });
        
        // Also prefetch the menu type courses (will be empty for a new menu type)
        queryClient.prefetchQuery({
          queryKey: ['menuTypeCourses', newMenuTypeId, language],
          queryFn: async () => {
            const timestamp = Date.now();
            return getMenuTypeCourses(newMenuTypeId, language, timestamp);
          }
        });
      }
      
      // After successful creation, auto-expand the new menu type
      setTimeout(() => {
        setExpandedMenuType(newMenuTypeId);
        
        // Scroll to the newly created menu type
        setTimeout(() => {
          if (menuTypeRefs.current[newMenuTypeId]) {
            menuTypeRefs.current[newMenuTypeId].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100);
      }, 100);
      
      toast.success(t('settings.menuTypeSaved'));
    } catch (error) {
      console.error('Error saving menu type:', error);
      toast.error(t('settings.errorSavingMenuType'));
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the school's default language
      const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code || 'en';
      
      // Check if the default language input is filled
      const defaultLangInput = e.target.elements[`translation-${defaultLangCode}`];
      const defaultLangValue = defaultLangInput ? defaultLangInput.value.trim() : '';
      
      if (!defaultLangValue) {
        // Show error if default language translation is missing
        toast.error(t('settings.defaultLanguageRequired', { language: t(`languages.${defaultLangCode}`) }));
        return;
      }

      // Collect translations from form - only include non-empty translations
      const translations = {};
      
      // Add translations for all languages with non-empty values
      selectedLanguages.forEach(lang => {
        const translationInput = e.target.elements[`translation-${lang.code}`];
        if (translationInput && translationInput.value.trim()) {
          translations[lang.code] = translationInput.value.trim();
        }
        // Skip empty translations entirely - don't add them to the object
      });
      
      // Use the default language translation as the main name
      const name = translations[defaultLangCode];
      
      // Get school ID using the getSchoolId helper function
      const schoolId = getSchoolId();
      
      // Create the course using React Query mutation
      await createCourseQuery.mutateAsync({ 
        schoolId, 
        name, 
        translations 
      });
      
      // Reset form
      selectedLanguages.forEach(lang => {
        const translationInput = e.target.elements[`translation-${lang.code}`];
        if (translationInput) {
          translationInput.value = '';
        }
      });
      
      toast.success(t('settings.courseSaved'));
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(t('settings.errorSavingCourse'));
    }
  };

  const deleteUser = (id) => {
    // To be implemented with actual API call
    setUsers(users.filter((user) => user.id !== id));
    toast.success(t('settings.userDeleted'));
  };

  const handleDeleteMenuType = async (id) => {
    try {
      // Optimistically update UI by removing the menu type from state
      setMenuTypes(prevMenuTypes => prevMenuTypes.filter(mt => mt.id !== id));
      
      // Then perform the actual API call
      await deleteMenuType(id);
      
      // No need to fetch menu types again - we've already updated the state optimistically
      // This prevents getting stale data from browser cache
      
      // Invalidate the availableMenuTypes cache in React Query
      const queryClient = window.__REACT_QUERY_GLOBAL_CLIENT__;
      if (queryClient) {
        console.log(`[DEBUG-MenuTypes] Invalidating availableMenuTypes cache after deleting menu type at ${new Date().toISOString()}`);
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
        });
        
        // Also invalidate publicMenu cache to ensure public-facing menus are updated
        console.log(`[DEBUG-MenuTypes] Invalidating publicMenu cache after deleting menu type at ${new Date().toISOString()}`);
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'publicMenu'
        });
      }
      
      setDeleteModalOpen(false);
      toast.success(t('settings.menuTypeDeleted'));
    } catch (error) {
      console.error('Error deleting menu type:', error);
      
      // Revert the optimistic update if there was an error
      const schoolId = getSchoolId();
      const updatedMenuTypes = await getMenuTypes(schoolId, currentLanguage);
      setMenuTypes(updatedMenuTypes);
      
      // Check if the error is because the menu type is in use
      if (error.message.includes('Cannot delete') || error.message.includes('in use')) {
        // Extract count from the error message if possible
        let errorCount = usageCount;
        const countMatch = error.message.match(/used in (\d+) menu/);
        if (countMatch && countMatch[1]) {
          errorCount = parseInt(countMatch[1], 10);
          // Update the usage count state
          setUsageCount(errorCount);
        }
        
        console.log(`Menu Type cannot be deleted - usageCount: ${errorCount}`); // Debug log
        
        // Ensure we're setting an object with type and count properties
        setDeleteError({
          type: 'menuType',
          count: errorCount
        });
      } else {
        toast.error(t('settings.errorDeletingMenuType'));
        setDeleteModalOpen(false);
      }
    }
  };

  const handleDeleteCourse = async (id) => {
    try {
      // Delete the course using React Query mutation
      await deleteCourseQuery.mutateAsync({ 
        id, 
        schoolId: getSchoolId() 
      });
      
      setDeleteModalOpen(false);
      toast.success(t('settings.courseDeleted'));
    } catch (error) {
      console.error('Error deleting course:', error);
      
      // Check if the error is because the course is in use
      if (error.message.includes('Cannot delete') || error.message.includes('in use')) {
        // Extract count from the error message if possible
        let errorCount = usageCount;
        const countMatch = error.message.match(/used in (\d+) menu/);
        if (countMatch && countMatch[1]) {
          errorCount = parseInt(countMatch[1], 10);
          // Update the usage count state
          setUsageCount(errorCount);
        }
        
        console.log(`Course cannot be deleted - usageCount: ${errorCount}`); // Debug log
        
        // Ensure we're setting an object with type and count properties
        setDeleteError({
          type: 'course',
          count: errorCount
        });
      } else {
        toast.error(t('settings.errorDeletingCourse'));
        setDeleteModalOpen(false);
      }
    }
  };

  // Update handleLogoUpload to use logo_url
  const handleLogoUpload = async (file) => {
    try {
      // Get school ID using the getSchoolId helper function
      const schoolId = getSchoolId();
      
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        return false;
      }
      
      if (!file) {
        console.error('No file provided for logo upload');
        return false;
      }
      
      console.log(`Uploading temporary logo to server: ${file.name} for school ${schoolId}`);
      
      setSchoolInfo(prev => ({ ...prev, isUploading: true }));
      
      // Use the schoolService method which correctly sets isTemporary=true
      const data = await schoolService.uploadSchoolLogo(file, schoolId, true);
      console.log('Logo upload successful:', data);
        
      // Store the server path for saving with school info
      setSchoolInfo(prev => ({
        ...prev,
        logoServerPath: data.logo_path,
        tempLogoPath: data.isTemporary ? data.logo_path : null, // Store temporary path separately
        logoPreview: data.logo_url || (data.logo_path ? getPublicUrl(data.logo_path) : null),
        isUploading: false
      }));
      
      return true;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(t('settings.errorUploadingLogo'));
      setSchoolInfo(prev => ({ ...prev, isUploading: false }));
      return false;
    }
  };

  // Improved debounce utility function with better timeout handling
  const debounce = (func, wait) => {
    let timeout;
    
    return function executedFunction(...args) {
      // Clear the previous timeout
      if (timeout) {
        clearTimeout(timeout);
      }
      
      // Set a new timeout
      timeout = setTimeout(() => {
        // Set timeout to null to indicate it's been executed
        timeout = null;
        // Call the original function with arguments
        func(...args);
      }, wait);
    };
  };

  // Update local state when the school settings data from the hook changes
  useEffect(() => {
    if (schoolSettingsData) {
      // Destructure relevant fields from the fetched data
      const {
        name = '',
        subdomain = '',
        homepage_url = '',
        default_language_id = null,
        default_menu_type_id = null,
        primary_bg_color = '#1E40AF',
        primary_text_color = '#FFFFFF',
        secondary_bg_color = '#3498DB',
        secondary_text_color = '#FFFFFF',
        logo_path = '',
        logo_url = '',
        custom_content = {},
        disclaimer_text = {},
        selected_languages = [],
        show_nutrients = false,
        show_tags = false,
        monthly_public_display = false,
        enable_public_month_navigation = false,
        months_ahead_limit = 3,
        months_back_limit = 1,
        days_after_month_start_for_back_navigation = 5,
        days_before_month_end_for_forward_navigation = 5,
        include_custom_content_in_image = true
      } = schoolSettingsData;
      
      // Update local state (SettingsPage specific state)
      setInitialSubdomain(subdomain);
      const updatedInfo = {
        name: name,
        subdomain: subdomain,
        homepageUrl: homepage_url,
        defaultLanguageId: default_language_id,
        defaultMenuTypeId: default_menu_type_id,
        primaryBgColor: primary_bg_color,
        primaryTextColor: primary_text_color,
        secondaryBgColor: secondary_bg_color,
        secondaryTextColor: secondary_text_color,
        logo: null,
        logoPreview: logo_path ? getPublicUrl(logo_path) : null,
        logoServerPath: logo_path,
        show_nutrients: show_nutrients,
        show_tags: show_tags,
        monthly_public_display: monthly_public_display,
        customContent: custom_content,
        disclaimerText: disclaimer_text,
        include_custom_content_in_image: include_custom_content_in_image,
        // Add month navigation settings
        enable_public_month_navigation: enable_public_month_navigation,
        months_ahead_limit: months_ahead_limit,
        months_back_limit: months_back_limit,
        days_after_month_start_for_back_navigation: days_after_month_start_for_back_navigation,
        days_before_month_end_for_forward_navigation: days_before_month_end_for_forward_navigation
      };
      
      console.log('[SettingsPage] Setting schoolInfo with show_tags:', updatedInfo.show_tags);
      setSchoolInfo(updatedInfo);

      // Update Redux language state using slice.actions
      dispatch(languageSlice.actions.setSelectedLanguages(selected_languages));
      dispatch(languageSlice.actions.setDefaultLanguageCode(default_language_id)); // Uncommented and updated
      console.log('[SettingsPage] Dispatched selectedLanguages to Redux:', selected_languages);
      console.log('[SettingsPage] Dispatched defaultLanguageCode to Redux:', default_language_id);

      // Update Redux branding state
      dispatch(updateBranding({
        primaryBgColor: primary_bg_color,
        primaryTextColor: primary_text_color,
        secondaryBgColor: secondary_bg_color,
        secondaryTextColor: secondary_text_color
      }));
    }
  }, [schoolSettingsData, dispatch]); // Removed schoolInfo dependencies as they were causing re-renders

  const handleSchoolInfoSubmit = async (e) => {
    e.preventDefault();

    try {
      // Set loading state
      setIsSubmittingSchoolInfo(true);

      // Get school ID from current_school object OR from SchoolContext
      const schoolId = getSchoolId();
      
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        setIsSubmittingSchoolInfo(false);
        return;
      }

      console.log('Preparing school info for submission:', schoolInfo);
      
      // Don't allow submission if subdomain is being checked or is invalid
      if (isCheckingSubdomain) {
        toast.error(t('settings.waitForSubdomainCheck'));
        setIsSubmittingSchoolInfo(false);
        return;
      }
      
      // If subdomain is changed, validate it with our comprehensive validator
      if (schoolInfo.subdomain !== initialSubdomain) {
        const validationResult = validateSubdomain(schoolInfo.subdomain);
        
        if (!validationResult.valid) {
          toast.error(validationResult.message);
          setIsSubmittingSchoolInfo(false);
          return;
        }
        
        // If client-side validation passes, check if backend validation passed
        if (subdomainStatus && !subdomainStatus.available) {
          toast.error(subdomainStatus.message || t('settings.subdomainNotAvailable'));
          setIsSubmittingSchoolInfo(false);
          return;
        }
        
        // Normalize the subdomain for consistent casing
        schoolInfo.subdomain = validationResult.normalizedSubdomain;
      }
      
      // Create a copy of schoolInfo without the logo File object
      // since we can't serialize the File object to JSON and it would cause errors
      const { logo, ...schoolInfoWithoutLogo } = schoolInfo;
      
      // Transform the schoolInfo object to match the API expected format
      const apiSchoolInfo = {
        name: schoolInfoWithoutLogo.name,
        subdomain: schoolInfoWithoutLogo.subdomain,
        homepage_url: schoolInfoWithoutLogo.homepageUrl || '',
        primary_bg_color: schoolInfoWithoutLogo.primaryBgColor,
        primary_text_color: schoolInfoWithoutLogo.primaryTextColor,
        secondary_bg_color: schoolInfoWithoutLogo.secondaryBgColor,
        secondary_text_color: schoolInfoWithoutLogo.secondaryTextColor,
        default_language_id: schoolInfoWithoutLogo.defaultLanguageId,
        default_menu_type_id: schoolInfoWithoutLogo.defaultMenuTypeId,
        show_nutrients: schoolInfoWithoutLogo.show_nutrients,
        show_tags: schoolInfoWithoutLogo.show_tags,
        monthly_public_display: schoolInfoWithoutLogo.monthly_public_display, // Add this line
        // Use the property name that the server expects (custom_content) for consistency
        custom_content: schoolInfoWithoutLogo.customContent,
        disclaimer_text: schoolInfoWithoutLogo.disclaimerText,
        include_custom_content_in_image: schoolInfoWithoutLogo.include_custom_content_in_image,
        // Add month navigation fields
        enable_public_month_navigation: schoolInfoWithoutLogo.enable_public_month_navigation,
        months_ahead_limit: parseInt(schoolInfoWithoutLogo.months_ahead_limit, 10) || 0,
        months_back_limit: parseInt(schoolInfoWithoutLogo.months_back_limit, 10) || 0,
        days_after_month_start_for_back_navigation: parseInt(schoolInfoWithoutLogo.days_after_month_start_for_back_navigation, 10) || 0,
        days_before_month_end_for_forward_navigation: parseInt(schoolInfoWithoutLogo.days_before_month_end_for_forward_navigation, 10) || 0
      };
      
      // If we have a logoServerPath, send it to the server
      // The backend will handle properly replacing any old logo and cleaning up temporary files
      if (schoolInfoWithoutLogo.logoServerPath) {
        apiSchoolInfo.logo_path = schoolInfoWithoutLogo.logoServerPath;
        console.log('Including logo path in request:', apiSchoolInfo.logo_path);
      } else if (schoolInfo.logo === null) {
        // Explicitly set to null to remove logo
        apiSchoolInfo.logo_path = null;
        console.log('Explicitly removing logo (setting to null)');
      }
      
      console.log('Sending school info to API:', apiSchoolInfo);
      
      // Direct update to Redux as a fallback - do this immediately
      // This helps ensure the UI updates even if other updates have timing issues
      dispatch(updateBranding({
        primaryBgColor: apiSchoolInfo.primary_bg_color || '#1E40AF',
        primaryTextColor: apiSchoolInfo.primary_text_color || '#FFFFFF',
        secondaryBgColor: apiSchoolInfo.secondary_bg_color || '#F3F4F6',
        secondaryTextColor: apiSchoolInfo.secondary_text_color || '#000000'
      }));
      console.log('[SettingsPage] Directly updated Redux branding state');
      
      try {
        // Use the React Query mutation instead of direct API call
        const response = await updateSchoolSettingsMutation.mutateAsync({
          settings: apiSchoolInfo, 
          schoolId
        });
        
        console.log('Server response:', response);
        
        // The updateBranding dispatch is now handled by the mutation hook
        
        // Handle subdomain change notification
        if (response.subdomainChanged) {
          toast.success(t('settings.subdomainChanged'));
          // Set cooldown status to inform the user they need to wait
          setCooldownActive(true);
          // Reset cooldown after 7 days (for demo purposes, we'll use a shorter time)
          setTimeout(() => setCooldownActive(false), 30000); // 30 seconds for demo
        } else {
          toast.success(t('settings.schoolInfoSaved'));
        }
        
        // If we got a logo_path or logo_url response, update the state
        if (response.logo_path || response.logo_url) {
          console.log('Updating logo info from response:', {
            logoPath: response.logo_path,
            logoUrl: response.logo_url || (response.logo_path ? getPublicUrl(response.logo_path) : null)
          });
          
          setSchoolInfo(prev => ({
            ...prev,
            logoServerPath: response.logo_path,
            tempLogoPath: null, // Clear the temporary path since it's now permanent
            logoPreview: response.logo_url || (response.logo_path ? getPublicUrl(response.logo_path) : null)
          }));
        } else if (apiSchoolInfo.logo_path === null) {
          // If we explicitly removed the logo
          setSchoolInfo(prev => ({
            ...prev,
            logoServerPath: null,
            tempLogoPath: null,
            logoPreview: ''
          }));
        }
        
        // Reset settings changed flag
        setIsSettingsChanged(false);
        
        // Update Redux directly one more time with the returned data
        dispatch(updateBranding({
          primaryBgColor: response.primary_bg_color || apiSchoolInfo.primary_bg_color || '#1E40AF',
          primaryTextColor: response.primary_text_color || apiSchoolInfo.primary_text_color || '#FFFFFF',
          secondaryBgColor: response.secondary_bg_color || apiSchoolInfo.secondary_bg_color || '#F3F4F6',
          secondaryTextColor: response.secondary_text_color || apiSchoolInfo.secondary_text_color || '#000000'
        }));
        console.log('[SettingsPage] Updated Redux with server response data');
        
        // No need to manually refresh data - React Query will handle that
      } catch (apiError) {
        console.error('API error when saving school info:', apiError);
        const errorMessage = apiError.message || t('settings.errorSavingSchoolInfo');
        toast.error(errorMessage);
        
        // Add more detailed error logging
        if (apiError.response) {
          console.error('Response details:', apiError.response);
        }
      }
    } catch (error) {
      console.error('Error saving school info:', error);
      toast.error(t('settings.errorSavingSchoolInfo'));
    } finally {
      // Reset loading state regardless of success/failure
      setIsSubmittingSchoolInfo(false);
    }
  };

  // Create a separate handler for custom content only
  const handleCustomContentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Set loading state
      setIsSubmittingSchoolInfo(true);
      
      // Get school ID from current_school object OR from SchoolContext
      const schoolId = getSchoolId();
      
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        setIsSubmittingSchoolInfo(false);
        return;
      }
      
      console.log('Submitting custom content:', schoolInfo.customContent);
      console.log('Submitting include_custom_content_in_image:', schoolInfo.include_custom_content_in_image);
      
      try {
        // Use the React Query mutation with just the custom content
        const payload = {
          settings: { 
            custom_content: schoolInfo.customContent,
            include_custom_content_in_image: schoolInfo.include_custom_content_in_image
          },
          schoolId
        };
        
        const response = await updateSchoolSettingsMutation.mutateAsync(payload);
        
        toast.success(t('settings.customContentSaved'));
        
        // Update custom content in state if it was returned
        if (response.custom_content) {
          setSchoolInfo(prev => ({
            ...prev,
            customContent: response.custom_content,
            include_custom_content_in_image: response.include_custom_content_in_image
          }));
        }
        
        // Reset settings changed flag
        setIsSettingsChanged(false);
        
        // Also directly update Redux with any branding data that came back
        if (response.primary_bg_color || response.primary_text_color || 
            response.secondary_bg_color || response.secondary_text_color) {
          dispatch(updateBranding({
            primaryBgColor: response.primary_bg_color || schoolInfo.primaryBgColor,
            primaryTextColor: response.primary_text_color || schoolInfo.primaryTextColor,
            secondaryBgColor: response.secondary_bg_color || schoolInfo.secondaryBgColor,
            secondaryTextColor: response.secondary_text_color || schoolInfo.secondaryTextColor
          }));
          console.log('[SettingsPage] Updated Redux with custom content response data');
        }
      } catch (apiError) {
        console.error('API error when saving custom content:', apiError);
        const errorMessage = apiError.message || t('settings.errorSavingCustomContent');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving custom content:', error);
      toast.error(t('settings.errorSavingCustomContent'));
    } finally {
      // Reset loading state regardless of success/failure
      setIsSubmittingSchoolInfo(false);
    }
  };
  
  // Create debounced versions with increased wait time to prevent rate limiting
  const debouncedHandleSchoolInfoSubmit = debounce(handleSchoolInfoSubmit, 1000);
  const debouncedHandleCustomContentSubmit = debounce(handleCustomContentSubmit, 1000);

  // Create a separate handler for disclaimer text only
  const handleDisclaimerTextSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Set loading state
      setIsSubmittingSchoolInfo(true);
      
      // Get school ID from current_school object OR from SchoolContext
      const schoolId = getSchoolId();
      
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        setIsSubmittingSchoolInfo(false);
        return;
      }
      
      console.log('Submitting disclaimer text:', schoolInfo.disclaimerText);
      
      try {
        // Use the React Query mutation with just the disclaimer text
        const response = await updateSchoolSettingsMutation.mutateAsync({
          settings: { 
            disclaimer_text: schoolInfo.disclaimerText 
          },
          schoolId
        });
        
        console.log('Server response for disclaimer text update:', response);
        
        toast.success(t('settings.disclaimerTextSaved'));
        
        // Update disclaimer text in state if it was returned
        if (response.disclaimer_text) {
          setSchoolInfo(prev => ({
            ...prev,
            disclaimerText: response.disclaimer_text
          }));
        }
        
        // Reset settings changed flag
        setIsSettingsChanged(false);
      } catch (apiError) {
        console.error('API error when saving disclaimer text:', apiError);
        const errorMessage = apiError.message || t('settings.errorSavingDisclaimerText');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving disclaimer text:', error);
      toast.error(t('settings.errorSavingDisclaimerText'));
    } finally {
      // Reset loading state regardless of success/failure
      setIsSubmittingSchoolInfo(false);
    }
  };
  
  // Create debounced disclaimer text handler after function definition
  const debouncedHandleDisclaimerTextSubmit = debounce(handleDisclaimerTextSubmit, 1000);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs separately
    if (type === 'checkbox') {
      setSchoolInfo(prevState => {
        const newState = {
          ...prevState,
        [name]: checked,
        };
        return newState;
      });
    } else {
      // Validate number fields related to month navigation
      if (type === 'number' && name.includes('month')) {
        // For months_ahead_limit and months_back_limit, enforce 0-12 range
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
          toast.error(t('monthNavigation.invalidNumber'));
          return;
        }
        
        if (numValue < 0) {
          toast.error(t('monthNavigation.mustBePositive'));
          return;
        }
        
        if (numValue > 12) {
          toast.error(t('monthNavigation.tooManyMonths', { max: 12 }));
          return;
        }
      } else if (type === 'number' && name.includes('days')) {
        // For day-related fields, enforce 0-31 range
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
          toast.error(t('monthNavigation.invalidNumber'));
          return;
        }
        
        if (numValue < 0) {
          toast.error(t('monthNavigation.mustBePositive'));
          return;
        }
        
        if (numValue > 31) {
          toast.error(t('monthNavigation.tooManyDays', { max: 31 }));
          return;
        }
      }
      
      // Handle text/number/color inputs
    setSchoolInfo({
      ...schoolInfo,
      [name]: value,
    });
    }
    
    // Mark settings as changed for the save button state
    setIsSettingsChanged(true);
  };

  const handleCustomContentChange = (e) => {
    const { name, value } = e.target;
    // Extract language code from name (e.g., "customContent_en" -> "en")
    const langCode = name.split('_')[1];
    
    // Only update state if value has actually changed
    setSchoolInfo(prev => {
      const currentValue = prev.customContent?.[langCode] || '';
      if (currentValue === value) return prev;
      
      return {
        ...prev,
        customContent: {
          ...prev.customContent,
          [langCode]: value
        }
      };
    });
  };

  const handleDisclaimerTextChange = (e) => {
    const { name, value } = e.target;
    // Extract language code from name (e.g., "disclaimerText_en" -> "en")
    const langCode = name.split('_')[1];
    
    // Only update state if value has actually changed
    setSchoolInfo(prev => {
      const currentValue = prev.disclaimerText?.[langCode] || '';
      if (currentValue === value) return prev;
      
      return {
        ...prev,
        disclaimerText: {
          ...prev.disclaimerText,
          [langCode]: value
        }
      };
    });
  };

  const openEditModal = async (type, item) => {
    try {
      setEditModalType(type);
      setEditModalItem(item);
      
      let translationsData = {};
      
      if (type === 'menuType') {
        console.log(`[DEBUG-MenuTypes] Fetching fresh translations for menu type ${item.id}`);
        translationsData = await menuTypeService.getMenuTypeTranslations(item.id);
        // Set the visibility state based on the menu type's is_visible property
        setMenuTypeVisibility(item.is_visible !== false);
        // Set the show_nutrients and show_tags states based on the menu type's properties
        setMenuTypeShowNutrients(item.show_nutrients !== false);
        setMenuTypeShowTags(item.show_tags !== false);
      } else if (type === 'course') {
        // Use React Query's useCourse hook to get course translations
        // Since we're in a function, we need to use the queryClient directly
        const queryClient = window.__REACT_QUERY_GLOBAL_CLIENT__;
        if (queryClient) {
          // Try to get from cache first
          const cachedData = queryClient.getQueryData(['course', item.id]);
          if (cachedData) {
            translationsData = { translations: [] };
            // Convert from object format to array format for consistency
            Object.entries(cachedData).forEach(([code, text]) => {
              translationsData.translations.push({ code, name: text });
            });
          } else {
            // If not in cache, fetch directly and add to cache
            const courseData = await courseService.getCourseTranslations(item.id);
            translationsData = courseData;
            queryClient.setQueryData(['course', item.id], translationsData);
          }
        } else {
          // Fallback to direct API call if queryClient is not available
          translationsData = await courseService.getCourseTranslations(item.id);
        }
      }
      
      // Get existing translations
      const existingTranslations = translationsData.translations || [];
      
      // Convert to the format expected by the modal
      const translationsObj = {};
      
      // Make sure all selected languages are included
      selectedLanguages.forEach(lang => {
        // Find existing translation for this language
        const existingTrans = existingTranslations.find(t => t.code === lang.code);
        
        if (existingTrans && existingTrans.name) {
          translationsObj[lang.code] = {
            text: existingTrans.name,
            code: lang.code,
            isOriginal: true
          };
        } else {
          // Create empty translation for this language (no fallback)
          translationsObj[lang.code] = {
            text: '', // Empty string - no fallback
            code: lang.code,
            isOriginal: true
          };
        }
      });
      
      // Store both formats - array for the submit function, object for the modal display
      setTranslations(Object.values(translationsObj).map(t => ({
        code: t.code,
        name: t.text,
        language_name: selectedLanguages.find(l => l.code === t.code)?.name || t.code,
        isOriginal: t.isOriginal
      })));
      setCurrentTranslations(translationsObj);
      setEditModalOpen(true);
    } catch (error) {
      console.error(`Error fetching ${type} translations:`, error);
      toast.error(t('settings.errorFetchingTranslations'));
    }
  };

  const closeModal = () => {
    setEditModalOpen(false);
    setEditModalItem(null);
    setTranslations([]);
    setMenuTypeVisibility(true); // Reset visibility to default
    setMenuTypeShowNutrients(true); // Reset show_nutrients to default
    setMenuTypeShowTags(true); // Reset show_tags to default
  };

  const handleTranslationChange = (langCode, value) => {
    // Update the translations array (used for form submission)
    setTranslations(
      translations.map((trans) => 
        trans.code === langCode ? { 
          ...trans, 
          name: value,
          // Once user edits a field, it becomes an original translation
          isOriginal: value.trim() !== '' 
        } : trans
      )
    );
    
    // Also update the currentTranslations object (used for modal display)
    setCurrentTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        text: value,
        isOriginal: value.trim() !== ''
      }
    }));
  };

  // Add function to handle editing a nutrient
  const handleEditNutrient = async (nutrient) => {
    try {
      const schoolId = getSchoolId();
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        return;
      }
      
      await updateNutrientMutation.mutateAsync({
        nutrientId: nutrient.id,
        nutrient: {
          is_enabled: nutrient.is_enabled,
          translations: nutrient.translations
        },
        schoolId
      });
      
      toast.success(t('settings.nutrientUpdated', 'Nutrient updated successfully'));
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating nutrient:', error);
      setEditError(t('settings.errorUpdatingNutrient', 'Error updating nutrient.'));
    }
  };

  // Add nutrient case to handleEditSubmit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the school's default language
      const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code || 'en';
      
      // Create translations object - only include non-empty translations
      const translationsObj = {};
      translations.forEach(trans => {
        // Only include non-empty translations in the object
        const trimmedValue = trans.name.trim();
        if (trimmedValue !== '') {
          translationsObj[trans.code] = trimmedValue;
        }
      });
      
      if (editModalType === 'menuType') {
        // Check if the default language translation is filled
        const defaultLangTrans = translations.find(trans => trans.code === defaultLangCode);
        if (!defaultLangTrans || !defaultLangTrans.name.trim()) {
          toast.error(t('settings.defaultLanguageRequired', { language: t(`languages.${defaultLangCode}`) }));
          return;
        }
        
        // Extract the name from the default language translation
        const name = translationsObj[defaultLangCode];
        
        console.log(`Updating menu type ${editModalItem.id} with name '${name}', translations:`, translationsObj, 
                    'visibility:', menuTypeVisibility,
                    'show_nutrients:', menuTypeShowNutrients,
                    'show_tags:', menuTypeShowTags);
        
        try {
          // Optimistically update UI state before API call
          if (editModalItem) {
            setMenuTypes(prevMenuTypes => 
              prevMenuTypes.map(mt => 
                mt.id === editModalItem.id 
                  ? {
                      ...mt,
                      name: translationsObj[defaultLangCode] || mt.name,
                      is_visible: menuTypeVisibility,
                      show_nutrients: menuTypeShowNutrients,
                      show_tags: menuTypeShowTags,
                      translations: Object.entries(translationsObj).map(([code, text]) => ({
                        code,
                        name: text
                      }))
                    }
                  : mt
              )
            );
          }
          
          // Use proper API function for updating menu types - include name, translations, and visibility
          await menuTypeService.updateMenuType(
            editModalItem.id, 
            translationsObj, 
            menuTypeVisibility,
            menuTypeShowNutrients, 
            menuTypeShowTags
          );
          
          toast.success(t('settings.menuTypeUpdated'));
          
          // No need to fetch menu types again - we've already updated the state optimistically
          // This prevents getting stale data from browser cache
          
          // Invalidate the availableMenuTypes cache in React Query
          const queryClient = window.__REACT_QUERY_GLOBAL_CLIENT__;
          if (queryClient) {
            console.log(`[DEBUG-MenuTypes] Invalidating availableMenuTypes cache after updating menu type at ${new Date().toISOString()}`);
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
            });
            
            // Also invalidate publicMenu cache
            console.log(`[DEBUG-MenuTypes] Invalidating publicMenu cache after updating menu type at ${new Date().toISOString()}`);
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === 'publicMenu'
            });
            
            // Also invalidate tag and nutrient queues
            console.log(`[DEBUG-MenuTypes] Invalidating tag and nutrient queues after updating menu type at ${new Date().toISOString()}`);
            queryClient.invalidateQueries({ queryKey: ['dishesNeedingTags'] });
            queryClient.invalidateQueries({ queryKey: ['nutrientQueue'] });
            
            // Set a flag in session storage to indicate that menu types have been updated
            sessionStorage.setItem('menuTypeUpdated', 'true');
            sessionStorage.setItem('menuTypeUpdatedTimestamp', Date.now().toString());
          }
        } catch (error) {
          console.error('Error updating menu type:', error);
          toast.error(t('settings.errorUpdatingMenuType'));
          
          // On error, fetch fresh data from the server to ensure UI consistency
          const schoolId = getSchoolId();
          const updatedMenuTypes = await getMenuTypes(schoolId, currentLanguage);
          setMenuTypes(updatedMenuTypes);
        }
      } else if (editModalType === 'course') {
        // Check if the default language translation is filled
        const defaultLangTrans = translations.find(trans => trans.code === defaultLangCode);
        if (!defaultLangTrans || !defaultLangTrans.name.trim()) {
          toast.error(t('settings.defaultLanguageRequired', { language: t(`languages.${defaultLangCode}`) }));
          return;
        }
        
        // Create translations object in the required format
        const translationsObj = {};
        translations.forEach(trans => {
          if (trans.name.trim()) {
            translationsObj[trans.code] = trans.name.trim();
          }
        });
        
        // Extract the name from the default language translation
        const name = translationsObj[defaultLangCode];
        
        console.log(`Updating course ${editModalItem.id} with name '${name}' and translations:`, translationsObj);
        
        try {
          // Get school ID for the mutation
          const schoolId = getSchoolId();
          
          // Update the course using the React Query mutation hook
          await updateCourseQuery.mutateAsync({
            id: editModalItem.id,
            schoolId: schoolId,
            name: name,
            translations: translationsObj
          });
          
          // Let React Query handle the cache invalidation
          // The mutation's onSettled handler will update all necessary caches
          // including the invalidation of public menu queries
          
          toast.success(t('settings.courseUpdated'));
        } catch (error) {
          console.error('Error updating course:', error);
          toast.error(t('settings.errorUpdatingCourse'));
        }
      } else if (editModalType === 'nutrient') {
        handleEditNutrient(editModalItem);
        return;
      }
      
      closeModal();
    } catch (error) {
      console.error(`Error updating ${editModalType}:`, error);
      toast.error(t(`settings.errorUpdating${editModalType.charAt(0).toUpperCase() + editModalType.slice(1)}`));
    }
  };

  const handleLanguageCheckboxChange = (languageId) => {
    setSelectedLanguages(prev => {
      // Check if this language is already selected
      const isSelected = prev.some(lang => lang.id === languageId);
      
      let updatedLanguages;
      if (isSelected) {
        // Remove it
        updatedLanguages = prev.filter(lang => lang.id !== languageId);
        
        // If we're removing the default language and there are other languages selected,
        // automatically update the defaultLanguageId to the first available language
        if (schoolInfo.defaultLanguageId === languageId && updatedLanguages.length > 0) {
          setSchoolInfo({
            ...schoolInfo,
            defaultLanguageId: updatedLanguages[0].id
          });
        }
      } else {
        // Add it
        const langToAdd = languages.find(lang => lang.id === languageId);
        updatedLanguages = [...prev, langToAdd];
        
        // If no default language is set, automatically set this as the default
        if (!schoolInfo.defaultLanguageId && langToAdd) {
          setSchoolInfo({
            ...schoolInfo,
            defaultLanguageId: langToAdd.id
          });
        }
      }
      
      return updatedLanguages;
    });
  };

  const handleSaveSelectedLanguages = async (e) => {
    e.preventDefault();
    
    try {
      setIsSavingLanguages(true);
      
      // Get school ID from current_school object OR from SchoolContext
      const schoolId = getSchoolId();
      
      if (!schoolId) {
        toast.error(t('settings.noSchoolSelected'));
        setIsSavingLanguages(false);
        return;
      }
      
      // Get the selected language IDs directly from the selectedLanguages state
      // This is more reliable than trying to find form elements since this isn't a form submission
      const languageIds = selectedLanguages.map(lang => lang.id);
      
      // Ensure at least one language is selected
      if (languageIds.length === 0) {
        toast.error(t('settings.atLeastOneLanguage'));
        setIsSavingLanguages(false);
        return;
      }
      
      // Get the default language ID from state
      const defaultLanguageId = schoolInfo.defaultLanguageId;
      
      // Ensure the default language is among the selected languages
      if (!languageIds.includes(defaultLanguageId)) {
        toast.error(t('settings.defaultMustBeSelected'));
        setIsSavingLanguages(false);
        return;
      }
      
      // First, update the default language in school settings
      try {
        await updateSchoolSettingsMutation.mutateAsync({
          settings: { default_language_id: defaultLanguageId },
          schoolId
        });
      } catch (error) {
        console.error('Error updating default language:', error);
        toast.error(t('settings.errorSavingDefaultLanguage'));
        setIsSavingLanguages(false);
        return;
      }
      
      // Then update the selected languages
      const updatedLanguages = await updateSelectedLanguages(languageIds, schoolId);
      
      if (updatedLanguages) {
        setSelectedLanguages(updatedLanguages);
        // Update Redux store
        dispatch({ type: 'language/setSelectedLanguages', payload: updatedLanguages });
        toast.success(t('settings.languagesSaved'));
        
        // Run language checks similar to SchoolContext
        // 1. Check if the user's preferred language is supported by this school now
        const userPreferredLanguage = languageService.getPreferredLanguage();
        const isUserLanguageSupported = updatedLanguages.some(
          lang => lang.code === userPreferredLanguage
        );
        
        // Find the default language object
        const defaultLanguage = languages.find(lang => lang.id === defaultLanguageId);
        const defaultLanguageCode = defaultLanguage?.code || 'en';
        
        console.log('[SettingsPage] Checking language settings after save:', {
          userPreferredLanguage,
          isUserLanguageSupported,
          defaultLanguageCode,
          updatedLanguages: updatedLanguages.map(l => `${l.code} (${l.id})`)
        });
        
        if (isUserLanguageSupported) {
          // If user's preferred language is now supported, switch to it
          console.log('[SettingsPage] User preferred language is supported, using:', userPreferredLanguage);
          await languageService.changeLanguage(userPreferredLanguage, dispatch, user && user.id ? true : false, true);
          
          // Clear any temporary language
          languageService.clearTemporaryLanguage();
        } else {
          // If not supported, switch to the default language
          console.log('[SettingsPage] User preferred language is not supported, switching to default:', defaultLanguageCode);
          await languageService.temporarilySwitchLanguage(defaultLanguageCode, dispatch);
        }
      }
    } catch (error) {
      console.error('Error saving selected languages:', error);
      toast.error(t('settings.errorSavingLanguages'));
    } finally {
      setIsSavingLanguages(false);
    }
  };

  // Function to open delete confirmation modal
  const openDeleteModal = async (type, item) => {
    setDeleteModalType(type);
    setDeleteModalItem(item);
    setDeleteError(null);
    setUsageCount(0);
    
    try {
      // Check if the item can be deleted by making a preliminary check
      let count = 0;
      
      if (type === 'menuType') {
        // Check menu items using this menu type
        const data = await getMenuTypeUsage(item.id);
        count = data.count;
        
        // Important: Set the usage count state
        setUsageCount(count);
        
        // If the item is in use, directly set error info with the count
        if (count > 0) {
          console.log(`Menu Type in use: ${count} items`); // Debug log
          setDeleteError({
            type: 'menuType',
            count: count // Use the local variable directly
          });
        }
      } else if (type === 'course') {
        // Check menu items using this course
        const data = await getCourseUsage(item.id);
        count = data.count;
        
        // Important: Set the usage count state
        setUsageCount(count);
        
        // If the item is in use, directly set error info with the count
        if (count > 0) {
          console.log(`Course in use: ${count} items`); // Debug log
          setDeleteError({
            type: 'course',
            count: count // Use the local variable directly
          });
        }
      } else if (type === 'nutrient') {
        // Set the delete modal properties
        setDeleteModalType('nutrient');
        setDeleteModalItem(item);
        setDeleteModalOpen(true);
        return;
      }
    } catch (error) {
      console.error(`Error checking ${type} usage:`, error);
    }
    
    // Open the modal after all data is set
    setDeleteModalOpen(true);
  };

  // Function to close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteModalType('');
    setDeleteModalItem(null);
    setDeleteError(null);
  };

  // Function to handle delete confirmation
  const confirmDelete = () => {
    if (deleteModalType === 'menuType' && deleteModalItem) {
      handleDeleteMenuType(deleteModalItem.id);
    } else if (deleteModalType === 'course' && deleteModalItem) {
      handleDeleteCourse(deleteModalItem.id);
    } else if (deleteModalType === 'nutrient') {
      deleteNutrientMutation.mutate(
        { 
          nutrientId: deleteModalItem.id,
          schoolId: getSchoolId()
        },
        {
          onSuccess: () => {
            toast.success(t('settings.nutrientDeleted', 'Nutrient deleted successfully'));
            setDeleteModalOpen(false);
          },
          onError: (error) => {
            console.error('Error deleting nutrient:', error);
            setDeleteError(t('settings.errorDeletingNutrient', 'Error deleting nutrient.'));
          }
        }
      );
      return;
    }
  };

  const handleAddCourseToMenuType = useCallback(async (menuTypeId) => {
    try {
      if (selectedAvailableCourse) {
        const courseToAdd = availableCourses.find(c => c.id === parseInt(selectedAvailableCourse));
        
        if (courseToAdd) {
          console.log(`[SettingsPage] Attempting to add course ${courseToAdd.id} (${courseToAdd.name}) to menu type ${menuTypeId}`);
          setSelectedAvailableCourse('');
          
          // Optimistically update local menuTypes state immediately
          setMenuTypes(prevMenuTypes => 
            prevMenuTypes.map(menuType => 
              menuType.id === menuTypeId 
                ? { ...menuType, courseCount: (menuType.courseCount || 0) + 1 }
                : menuType
            )
          );
          
          await addCourseToMenuTypeQuery.mutateAsync({
            menuTypeId,
            courseId: parseInt(selectedAvailableCourse),
            courseName: courseToAdd.name,
            schoolId: getSchoolId()
          });
          
          toast.success(t('settings.courseAddedToMenuType'));
        }
      }
    } catch (error) {
      console.error('Error adding course to menu type:', error);
      toast.error(t('settings.errorAddingCourseToMenuType'));
      
      // Revert optimistic update on error
      setMenuTypes(prevMenuTypes => 
        prevMenuTypes.map(menuType => 
          menuType.id === menuTypeId 
            ? { ...menuType, courseCount: Math.max((menuType.courseCount || 0) - 1, 0) }
            : menuType
        )
      );
    }
  }, [selectedAvailableCourse, t, availableCourses, addCourseToMenuTypeQuery, getSchoolId]);

  const handleRemoveCourseFromMenuType = useCallback(async (menuTypeId, courseId) => {
    try {
      // Find the course to be removed
      const courseToRemove = menuTypeCourses.find(c => c.id === courseId);
      
      if (courseToRemove) {
        console.log(`[SettingsPage] Attempting to remove course ${courseId} (${courseToRemove.name}) from menu type ${menuTypeId}`);
        
        // Optimistically update local menuTypes state immediately
        setMenuTypes(prevMenuTypes => 
          prevMenuTypes.map(menuType => 
            menuType.id === menuTypeId 
              ? { ...menuType, courseCount: Math.max((menuType.courseCount || 0) - 1, 0) }
              : menuType
          )
        );
        
        // Execute mutation with React Query
        await removeCourseFromMenuTypeQuery.mutateAsync({
          menuTypeId,
          courseId,
          courseName: courseToRemove.name,
          schoolId: getSchoolId()
        });
        
        toast.success(t('settings.courseRemovedFromMenuType'));
      }
    } catch (error) {
      console.error('Error removing course from menu type:', error);
      toast.error(t('settings.errorRemovingCourseFromMenuType'));
      
      // Revert optimistic update on error
      setMenuTypes(prevMenuTypes => 
        prevMenuTypes.map(menuType => 
          menuType.id === menuTypeId 
            ? { ...menuType, courseCount: (menuType.courseCount || 0) + 1 }
            : menuType
        )
      );
    }
  }, [t, menuTypeCourses, removeCourseFromMenuTypeQuery, getSchoolId]);

  const handleReorderCourse = useCallback(async (menuTypeId, courseId, direction) => {
    try {
      setIsSaving(true);
      console.log(`[SettingsPage] Attempting to reorder course ${courseId} in menu type ${menuTypeId}, direction: ${direction}`); // DEBUG LOG
      // Use the React Query mutation hook to handle reordering
      // The optimistic update is already built into the mutation
      await reorderMenuTypeCourseQuery.mutateAsync({
        menuTypeId,
        courseId,
        direction,
        schoolId: getSchoolId()
      });
      
      toast.success(t('settings.courseReordered'));
    } catch (error) {
      console.error('Error reordering course:', error);
      toast.error(t('settings.errorReorderingCourse'));
      // React Query will automatically handle refetching on errors
    } finally {
      setIsSaving(false);
    }
  }, [t, setIsSaving, reorderMenuTypeCourseQuery, getSchoolId]);

  // Add this useEffect to check subdomain availability when it changes
  useEffect(() => {
    // Clear previous timeout to avoid race conditions
    if (subdomainTimeoutRef.current) {
      clearTimeout(subdomainTimeoutRef.current);
    }
    
    // Reset status if subdomain is unchanged from initial value
    if (schoolInfo.subdomain === initialSubdomain) {
      setSubdomainStatus(null);
      return;
    }
    
    // Use our comprehensive validator
    const validationResult = validateSubdomain(schoolInfo.subdomain);
    if (!validationResult.valid) {
      setSubdomainStatus({ 
        available: false, 
        message: validationResult.message
      });
      return;
    }
    
    // Normalize the subdomain (client-side)
    const normalizedSubdomain = validationResult.normalizedSubdomain;
    
    // Get the current school ID
    const schoolId = getSchoolId();
    if (!schoolId) {
      console.error('Cannot check subdomain availability - no current school ID available');
      setSubdomainStatus({
        available: false,
        message: t('settings.errorCheckingSubdomain')
      });
      setIsCheckingSubdomain(false);
      return;
    }
    
    // If the client-side validation passes, debounce the API call (wait 500ms after typing stops)
    setIsCheckingSubdomain(true);
    subdomainTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`[SettingsPage] Checking subdomain availability for: ${normalizedSubdomain}, schoolId: ${schoolId}`);
        // Use the normalized subdomain for checking availability and pass the school ID
        const response = await schoolService.checkSubdomainAvailability(normalizedSubdomain, schoolId);
        setSubdomainStatus(response);
        
        // Handle cooldown information if present
        if (response.cooldown) {
          setCooldownInfo({
            daysRemaining: response.daysRemaining,
            cooldownEnds: new Date(response.cooldownEnds),
            message: response.message
          });
          setCooldownActive(true);
        } else {
          setCooldownInfo(null);
          setCooldownActive(false);
        }
      } catch (error) {
        console.error('Error checking subdomain:', error);
        setSubdomainStatus({ 
          available: false, 
          message: t('settings.subdomainCheckError')
        });
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500);
    
    // Cleanup the timeout on unmount or when dependencies change
    return () => {
      if (subdomainTimeoutRef.current) {
        clearTimeout(subdomainTimeoutRef.current);
      }
    };
  }, [schoolInfo.subdomain, initialSubdomain, t, getSchoolId]);

  // Add useEffect to check for cooldown period on component load
  useEffect(() => {
    const checkCooldownStatus = async () => {
      // Get school ID using the getSchoolId helper function
      const schoolId = getSchoolId();
      
      // Only check if we have a subdomain and school ID to check
      if (initialSubdomain && schoolId) {
        try {
          const response = await schoolService.checkSubdomainAvailability(initialSubdomain, schoolId);
          // If the school is in cooldown period, store the info
          if (response.cooldown) {
            setCooldownInfo({
              daysRemaining: response.daysRemaining,
              cooldownEnds: new Date(response.cooldownEnds),
              message: response.message
            });
            setCooldownActive(true);
          }
        } catch (error) {
          console.error('Error checking cooldown status:', error);
        }
      }
    };
    
    // Only run this check if initialSubdomain is set (after fetchSchoolSettings completes)
    if (initialSubdomain) {
      checkCooldownStatus();
    }
  }, [initialSubdomain, getSchoolId]);

  // Create a separate useEffect to ensure languages are loaded immediately on mount
  useEffect(() => {
    const loadAllLanguages = async () => {
      console.log('Loading all languages for the entire settings page');
      try {
        // Always fetch languages since they are system-wide
        const allLanguagesData = await getLanguages();
        console.log('All languages loaded:', allLanguagesData);
        setLanguages(allLanguagesData);
        
        // Get school ID using the getSchoolId helper function to ensure consistency
        const schoolId = getSchoolId();
        
        // Only fetch selected languages if we have a school ID
        if (schoolId) {
          try {
            const selectedLangsData = await getSelectedLanguages(schoolId);
            console.log('Selected languages loaded:', selectedLangsData);
            // Check if selectedLangsData is a valid array before setting state and dispatching
            if (selectedLangsData && Array.isArray(selectedLangsData)) {
              setSelectedLanguages(selectedLangsData);
              // Update Redux store with a valid action object
              dispatch({ type: 'language/setSelectedLanguages', payload: selectedLangsData });
            }
          } catch (selLangError) {
            console.error('Error loading selected languages:', selLangError);
            // Don't throw here to allow the overall function to continue
          }
        }
      } catch (error) {
        console.error('Error loading all languages:', error);
      }
    };
    
    // Load languages right away, don't wait for any specific tab
    loadAllLanguages();
  }, [dispatch, getSchoolId]); // Add getSchoolId to dependencies
  
  // Restore the language loading effect
  // Create separate effect for languages loading when tab changes to languages
  useEffect(() => {
    if (activeTab === 'languages') {
      const loadLanguagesData = async () => {
        console.log('Loading languages data for languages tab');
        try {
          // Always fetch languages since they are system-wide
          const allLanguagesData = await getLanguages();
          console.log('All languages:', allLanguagesData);
          setLanguages(allLanguagesData);
          
          // Get school ID using the getSchoolId helper function to ensure consistency
          const schoolId = getSchoolId();
          
          // Only fetch selected languages if we have a school ID
          if (schoolId) {
            try {
              const selectedLangsData = await getSelectedLanguages(schoolId);
              console.log('Selected languages loaded:', selectedLangsData);
              // Check if selectedLangsData is a valid array before setting state and dispatching
              if (selectedLangsData && Array.isArray(selectedLangsData)) {
                setSelectedLanguages(selectedLangsData);
                // Update Redux store with a valid action object
                dispatch({ type: 'language/setSelectedLanguages', payload: selectedLangsData });
              }
            } catch (selLangError) {
              console.error('Error loading selected languages:', selLangError);
              // Don't throw here to allow the overall function to continue
            }
          }
        } catch (error) {
          console.error('Error loading languages data:', error);
        }
      };
      
      loadLanguagesData();
    }
  }, [activeTab, user, school, dispatch, getSchoolId, t]);

  // Fetch menu types when needed (Consolidated Logic)
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const fetchMenuTypes = async () => {
      try {
        setIsMenuTypesLoading(true);
        const schoolId = getSchoolId();
        const languageId = currentLanguage || 'en';
        
        // Use the schoolId as the first parameter
        const data = await getMenuTypes(schoolId, languageId);
        
        if (isMounted) {
          setMenuTypes(data);
          setIsMenuTypesLoading(false);
        }
      } catch (error) {
        console.error('Error fetching menu types:', error);
        if (isMounted) {
          toast.error(t('settings.errorFetchingMenuTypes'));
          setIsMenuTypesLoading(false);
        }
      }
    };
    
    // Get school ID from SchoolContext or fallback to user context
    const schoolId = getSchoolId();
    
    // Fetch menu types immediately on page load to determine guidance banner state
    if (schoolId) {
      fetchMenuTypes();
    }
    
    // Cleanup function to handle component unmounting
    return () => {
      isMounted = false;
    };
  }, [getSchoolId, currentLanguage, t]); // Removed activeTab condition - now loads on page load

  // Fetch users when the component mounts or the current school changes
  useEffect(() => {
    // Only update the loading state when it changes to prevent re-renders
    if (loadingUsers !== isUsersLoading) {
      setLoadingUsers(isUsersLoading);
    }
    
    // Only show the error message once
    if (isUsersError) {
      // Using console.error once is fine
      console.error('Error fetching users with React Query');
      // Don't show toast messages on every render
      if (!errorShownRef.current) {
        // Only show error toast if we're not in the initial loading state
        // This prevents showing errors during the first load
        if (!isUsersLoading && usersData === undefined) {
          toast.error(t('settings.errorFetchingUsers'));
          setLoadingUsers(false);
          errorShownRef.current = true;
        }
      }
    } else {
      // Reset the ref when there's no error
      errorShownRef.current = false;
    }
  }, [usersData, isUsersLoading, isUsersError, t, loadingUsers]);

  // Check current user permissions for this school
  useEffect(() => {
    // Get the schoolId using the getSchoolId helper function
    const schoolId = getSchoolId();
    
    if (schoolId && userSchools) {
      // Get user's roles for the current school from fresh data
      const userSchool = userSchools.find(s => s.id === schoolId);
      const userRoles = userSchool?.roles || [];
      
      // Set permissions flags
      const isAdmin = userRoles.includes('Admin');
      const isSuperadmin = userRoles.includes('Superadmin');
      const hasAdminPrivileges = isAdmin || isSuperadmin;
      
      setUserPermissions({
        isAdmin,
        isSuperadmin,
        hasAdminPrivileges
      });
      
      console.log('User permissions updated for school:', schoolId, {
        isAdmin,
        isSuperadmin,
        hasAdminPrivileges,
        userRoles
      });
    }
  }, [userSchools, getSchoolId]);

  // Update courses state once when React Query data changes
  useEffect(() => {
    // We use a ref to track if we need to update the courses state
    // This helps avoid infinite re-renders
    const shouldUpdate = coursesData !== courses && 
      // Make sure we're not in a loading state
      !isCoursesLoading && 
      // Make sure we didn't error out
      !isCoursesError;
  
    if (shouldUpdate) {
      setCourses(coursesData);
      
      // For new users with no courses, automatically select the courses tab
      if (coursesData.length === 0 && activeTab === 'users') {
        setActiveTab('courses');
      }
    }
  }, [coursesData, isCoursesLoading, isCoursesError, courses, activeTab]);

  // Add a handler for default language changes
  const handleDefaultLanguageChange = (e) => {
    const defaultLanguageId = e.target.value ? Number(e.target.value) : null;
    
    // Update local state
    setSchoolInfo({
      ...schoolInfo,
      defaultLanguageId
    });
    
    // Only update in the backend if we have a valid language ID and school ID
    if (defaultLanguageId && schoolId) {
      // Use React Query mutation to update the setting
      updateSchoolSettingsMutation.mutate({
        settings: { default_language_id: defaultLanguageId },
        schoolId
      }, {
        onSuccess: () => {
          console.log(`[SettingsPage] Default language updated to ID: ${defaultLanguageId}`);
          // No need for toast notification here as it's an automatic update
        },
        onError: (error) => {
          console.error('[SettingsPage] Error updating default language:', error);
          // Do not show error toast here as the user can still save manually
        }
      });
    }
  };

  // Check for newly created schools and set up initial guidance
  useEffect(() => {
    // Check if this is a newly created school
    const lastCreatedSchool = sessionStorage.getItem('lastCreatedSchool');
    
    if (lastCreatedSchool && school?.subdomain) {
      try {
        const schoolInfo = JSON.parse(lastCreatedSchool);
        const isRecentlyCreated = (Date.now() - schoolInfo.timestamp) < 300000; // 5 minutes
        
        // If school was created recently and subdomain matches current school
        if (isRecentlyCreated && schoolInfo.subdomain === school.subdomain) {
          console.log('[SettingsPage] Detected newly created school:', schoolInfo);
          
          // Set the tab to courses for new schools
          setActiveTab('courses');
          
          // Show a welcome toast
          toast.success(t('settings.welcomeNewSchool', 'Welcome to your new school! Let\'s set everything up.'));
          
          // Clear the lastCreatedSchool immediately to prevent repeated processing
            sessionStorage.removeItem('lastCreatedSchool');
        }
      } catch (e) {
        console.error('[SettingsPage] Error handling newly created school:', e);
      }
    }
  }, [school?.subdomain]);

  const isCombinedLoading = isSchoolSettingsLoading || isCoursesLoading || isMenuTypesLoading || isUsersLoading;
  const isAnyError = schoolSettingsError || isCoursesError || isUsersError; // Note: MenuTypes error handled separately

  // Calculate if any menu type has courses assigned
  const hasMenuTypeCourses = useMemo(() => {
    if (!menuTypes || menuTypes.length === 0) {
      console.log('[GuidanceBanner] hasMenuTypeCourses: false (no menu types)');
      return false;
    }
    // Check if *any* menu type in the array has a courseCount > 0
    const result = menuTypes.some(mt => mt.courseCount && mt.courseCount > 0);
    console.log('[GuidanceBanner] hasMenuTypeCourses calculation:', {
      menuTypesCount: menuTypes.length,
      menuTypesWithCourses: menuTypes.filter(mt => mt.courseCount && mt.courseCount > 0).length,
      result,
      menuTypesData: menuTypes.map(mt => ({ id: mt.id, name: mt.name, courseCount: mt.courseCount }))
    });
    return result;
  }, [menuTypes]);

  // Add nutrients state
  const [nutrients, setNutrients] = useState([]);
  const [newNutrient, setNewNutrient] = useState({ 
    translations: {}, 
    is_enabled: true 
  });

  // Handle nutrient translation change
  const handleNutrientTranslationChange = (langCode, value) => {
    setNewNutrient(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: value
      }
    }));
  };

  // Handle nutrient form submission
  const handleNutrientSubmit = async (e) => {
    e.preventDefault();
    
    // Get school ID
    const schoolId = getSchoolId();
    if (!schoolId) {
      toast.error(t('settings.noSchoolSelected'));
      return;
    }
    
    try {
      // Create nutrient data object
      const nutrientData = {
        is_enabled: newNutrient.is_enabled,
        translations: newNutrient.translations
      };
      
      // Create the nutrient
      await createNutrientMutation.mutateAsync({
        nutrient: nutrientData,
        schoolId
      });
      
      // Reset form
      setNewNutrient({
        translations: {},
        is_enabled: true
      });
      
    } catch (error) {
      console.error('Error creating nutrient:', error);
      toast.error(t('settings.errorCreatingNutrient', 'Error creating nutrient. Please try again.'));
    }
  };

  return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-3xl font-bold mb-6">{t('settings.title')}</h1>
        
        {/* Guidance banner for new users with no courses - only show when data is loaded */}
        {!isCoursesLoading && coursesData && coursesData.length === 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('settings.guidanceCourses.title', 'Welcome to your new school!')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t('settings.guidanceCourses.description', 'Your first step is to create courses. Courses represent different parts of a meal (like "Main dish", "Side dish", "Dessert", etc.). Create courses in the "Courses" tab below.')}
                  </p>
                  <p className="mt-2">
                    {t('settings.guidanceCourses.examples', 'Examples: Main Course, Salad, Soup, Dessert, Bread, Beverages, Snack')}
                  </p>
                  <button 
                    onClick={() => setActiveTab('courses')}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {t('settings.guidanceAction.createCourses', 'Go to Courses Tab')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Guidance banner for users with courses but no menu types or no courses assigned to menu types - only show when data is loaded */}
        {(() => {
          const greenBannerCondition = !isCoursesLoading && !isMenuTypesLoading && coursesData && coursesData.length > 0 && 
                                     menuTypes && (menuTypes.length === 0 || !hasMenuTypeCourses);
          console.log('[GuidanceBanner] Green banner evaluation:', {
            isCoursesLoading,
            isMenuTypesLoading,
            coursesCount: coursesData?.length || 0,
            menuTypesCount: menuTypes?.length || 0,
            hasMenuTypeCourses,
            greenBannerCondition
          });
          return greenBannerCondition;
        })() && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {t('settings.guidanceMenuTypes.title', 'Great! Now create Menu Types')}
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>
                    {t('settings.guidanceMenuTypes.description', 'Menu Types represent different kinds of menus your school offers (like "Lunch", "Breakfast", "Special Diet"). Create menu types in the "Menu Types" tab and assign courses to them.')}
                  </p>
                  <p className="mt-2">
                    {t('settings.guidanceMenuTypes.examples', 'Examples: Standard Lunch, Vegetarian Menu, Breakfast, Special Diet, After School Snack')}
                  </p>
                  <p className="mt-2">
                    {t('settings.guidanceMenuTypes.courses', 'Once you have created menu types, you should assign courses to them by clicking on the downward arrow in the menu types list.')}
                  </p>
                  <button 
                    onClick={() => setActiveTab('menuTypes')}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    {t('settings.guidanceAction.createMenuTypes', 'Go to Menu Types Tab')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Purple guidance banner for users ready to create their first menu - show when courses are assigned to menu types but no menus exist */}
        {(() => {
          const purpleBannerCondition = !isCoursesLoading && !isMenuTypesLoading && !isMenusLoading && 
                                      coursesData && coursesData.length > 0 && 
                                      menuTypes && menuTypes.length > 0 && hasMenuTypeCourses && 
                                      menusData && menusData.length === 0;
          console.log('[GuidanceBanner] Purple banner evaluation:', {
            isCoursesLoading,
            isMenuTypesLoading,
            isMenusLoading,
            coursesCount: coursesData?.length || 0,
            menuTypesCount: menuTypes?.length || 0,
            hasMenuTypeCourses,
            menusCount: menusData?.length || 0,
            purpleBannerCondition
          });
          return purpleBannerCondition;
        })() && (
          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6 rounded shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-purple-800">
                  {t('settings.guidanceMenus.title', 'Perfect! You\'re all set to create your first menu')}
                </h3>
                <div className="mt-2 text-sm text-purple-700">
                  <p>
                    {t('settings.guidanceMenus.description', 'You have successfully created courses and menu types, and assigned courses to menu types. Now you can create your first menu and start adding dishes for each day.')}
                  </p>
                  <button 
                    onClick={() => {
                      // Navigate to the menu editor
                      const schoolSubdomain = school?.subdomain;
                      if (schoolSubdomain) {
                        window.location.href = `${window.location.protocol}//${window.location.host}/${schoolSubdomain}/admin/menu-editor`;
                      } else {
                        navigate('/admin/menu-editor');
                      }
                    }}
                    className="mt-3 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    {t('settings.guidanceAction.createFirstMenu', 'Create Your First Menu')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <style>{noOutlineStyles}</style>
          <div className="border-b">
            <nav className="flex flex-wrap no-outline-tabs">
            <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'users' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('users');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
            >
              {t('settings.users')}
            </button>
              
            <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'menuTypes' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('menuTypes');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
            >
              {t('settings.menuTypes')}
            </button>
              
            <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'courses' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('courses');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
            >
              {t('settings.courses')}
            </button>
              
            <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'languages' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('languages');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
              >
                {t('settings.languages')}
              </button>
              
            <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'nutrients' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('nutrients');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
              >
                {t('settings.nutrients')}
              </button>
              
              <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'tags' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('tags');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
              >
                {t('settings.tags', 'Tags')}
              </button>
              
              <button
                className={`relative py-3 px-6 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus:outline-none focus:ring-0 select-none
                  ${activeTab === 'schoolInfo' ? 
                    'text-blue-600 border-b-2 border-blue-600 font-semibold' : 
                    'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                onClick={() => {
                  // Store current scroll position before changing tab
                  const scrollPosition = window.scrollY;
                  setActiveTab('schoolInfo');
                  // Restore scroll position after state update
                  setTimeout(() => window.scrollTo(0, scrollPosition), 0);
                }}
                style={{ outline: 'none' }}
            >
              {t('settings.schoolInfo')}
            </button>
            </nav>
          </div>
          
          <div className="p-6">
            {activeTab === 'users' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('settings.manageUsers')}</h2>
                
                {!userPermissions.hasAdminPrivileges ? (
                  // Permission denied message for non-admin users
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded mb-4">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>{t('settings.accessRestricted', 'You do not have permission to view or manage users. Only administrators can access this section.')}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Role-based access control explanation */}
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h3 className="font-semibold text-blue-800 mb-1">{t('settings.roleExplanation')}</h3>
                      <ul className="list-disc pl-5 text-sm text-blue-700">
                        <li><strong>{t('settings.superadmin')}:</strong> {t('settings.superadminDesc')}</li>
                        <li><strong>{t('settings.admin')}:</strong> {t('settings.adminDesc')}</li>
                        <li><strong>{t('settings.editor')}:</strong> {t('settings.editorDesc')}</li>
                        <li><strong>{t('settings.viewer')}:</strong> {t('settings.viewerDesc')}</li>
                      </ul>
                    </div>
                    
                    {/* Invite User Button */}
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={openInviteModal}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                        </svg>
                        {t('settings.inviteUser')}
                      </button>
                    </div>
                    
                    {/* Users Table */}
                <div className="overflow-x-auto">
                      {loadingUsers ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                      ) : (
                        !usersData || usersData.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            {t('settings.noUsers')}
                          </div>
                        ) : (
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-200 text-gray-600 text-sm leading-normal">
                        <th className="py-3 px-6 text-left">{t('settings.name')}</th>
                        <th className="py-3 px-6 text-left">{t('settings.email')}</th>
                                <th className="py-3 px-6 text-left">{t('settings.roles')}</th>
                        <th className="py-3 px-6 text-left">{t('settings.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm">
                              {usersData.map((userItem) => {
                                // Get roles for this user in this specific school
                                console.log('[DEBUG] Rendering user item:', userItem);
                                const userRolesForSchool = userItem.roles || [];
                                const hasAdminRole = userRolesForSchool.includes('Admin') || userRolesForSchool.includes('Superadmin');
                                const isSuperadmin = userRolesForSchool.includes('Superadmin');
                                console.log('[DEBUG] User roles for displayed user:', userRolesForSchool);
                                console.log('[DEBUG] Has admin role?', hasAdminRole);
                                
                                // Check if current user can edit this user's roles
                                // user (from useUser hook) represents the currently logged-in user
                                console.log('[DEBUG] Checking current user schools:', user?.schools);
                                
                                // IMPORTANT FIX: Always use school ID from context, not user.current_school
                                // This ensures we check roles for the correct school
                                const currentSchoolId = school?.id || user?.current_school?.id;
                                const loggedInUserRolesForSchool = user?.schools
                                  ?.find(school => school.id === currentSchoolId)?.roles || [];
                                
                                console.log('[DEBUG] Current user roles for school:', loggedInUserRolesForSchool);
                                const currentUserIsSuperadmin = loggedInUserRolesForSchool.includes('Superadmin');
                                const currentUserIsAdmin = loggedInUserRolesForSchool.includes('Admin');
                                console.log('[DEBUG] Current user is superadmin?', currentUserIsSuperadmin);
                                
                                // Fix for role checking logic:
                                // 1. Superadmins can edit any user
                                // 2. Admins can only edit non-admin users
                                let canEditRoles = false;
                                if (currentUserIsSuperadmin) {
                                  // Superadmins can edit any user
                                  canEditRoles = true;
                                } else if (currentUserIsAdmin) {
                                  // Admins can only edit non-admin users
                                  canEditRoles = !hasAdminRole;
                                }
                                
                                console.log('[DEBUG] Can edit roles?', canEditRoles);
                                
                                return (
                                  <tr key={userItem.id} className="border-b border-gray-200 hover:bg-gray-100">
                                    <td className="py-3 px-6 text-left">{userItem.name}</td>
                                    <td className="py-3 px-6 text-left">{userItem.email}</td>
                          <td className="py-3 px-6 text-left">
                                      <div className="flex flex-wrap gap-1">
                                        {userRolesForSchool.map((role) => (
                                          <span 
                                            key={role} 
                                            className={`px-2 py-1 rounded-full text-xs font-semibold 
                                              ${role === 'Superadmin' ? 'bg-purple-100 text-purple-800' : 
                                                role === 'Admin' ? 'bg-red-100 text-red-800' : 
                                                role === 'Editor' ? 'bg-green-100 text-green-800' : 
                                                'bg-gray-100 text-gray-800'}`}
                                          >
                                            {t(`settings.${role.toLowerCase()}`)}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="py-3 px-6 text-left">
                                      {canEditRoles && (
                            <button
                              className="text-blue-500 hover:text-blue-700 mr-2"
                                          onClick={() => openEditUserModal(userItem)}
                            >
                              {t('settings.edit')}
                            </button>
                                      )}
                                      {canEditRoles && (
                            <button
                              className="text-red-500 hover:text-red-700"
                                          onClick={() => handleRemoveUser(userItem.id, hasAdminRole, userItem.email)}
                            >
                                          {t('settings.remove')}
                            </button>
                                      )}
                                      {!canEditRoles && (
                                        <span className="text-gray-400">{t('settings.restrictedAccess')}</span>
                                      )}
                          </td>
                        </tr>
                                );
                              })}
                    </tbody>
                  </table>
                        )
                      )}
                </div>
                  </>
                )}
                
                {/* Invite User Modal */}
                {inviteModalOpen && userPermissions.hasAdminPrivileges && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h3 className="text-xl font-semibold mb-4">{t('settings.inviteUser')}</h3>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.email')}
                        </label>
                      <input
                          type="email"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                          placeholder={t('settings.enterEmail')}
                      />
                    </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.selectRoles')}
                        </label>
                        <div className="space-y-2">
                          {['Viewer', 'Editor', 'Admin', 'Superadmin'].map((role) => {
                            // Check if current user can assign this role
                            const isSuperadminRole = role === 'Superadmin';
                            const isAdminRole = role === 'Admin';
                            
                            // Update to use current_school specific roles
                            console.log('[DEBUG] Checking role access for:', role);
                            console.log('[DEBUG] User schools in role checkbox:', user?.schools);
                            
                            // IMPORTANT FIX: Always use school ID from context, not user.current_school
                            const currentSchoolId = school?.id || user?.current_school?.id;
                            const userRolesForCurrentSchool = user?.schools
                              ?.find(school => school.id === currentSchoolId)?.roles || [];
                            
                            console.log('[DEBUG] Current user roles for school in checkbox:', userRolesForCurrentSchool);
                            const currentUserIsSuperadmin = userRolesForCurrentSchool.includes('Superadmin');
                            console.log('[DEBUG] Is current user superadmin in checkbox?', currentUserIsSuperadmin);
                            
                            // Apply the same permission rules as for editing roles:
                            // 1. Superadmins can assign any role
                            // 2. Admins cannot assign Admin or Superadmin roles
                            const canAssignRole = currentUserIsSuperadmin ? true : !(isSuperadminRole || isAdminRole);
                            console.log('[DEBUG] Can assign role?', canAssignRole, 'for role:', role);
                            
                            return (
                              <div key={role} className="flex items-center">
                      <input
                                  type="checkbox"
                                  id={`role-${role}`}
                                  checked={selectedRoles.includes(role)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedRoles([...selectedRoles, role]);
                                    } else {
                                      setSelectedRoles(selectedRoles.filter(r => r !== role));
                                    }
                                  }}
                                  disabled={!canAssignRole}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                                <label htmlFor={`role-${role}`} className={`ml-2 block text-sm text-gray-900 ${!canAssignRole ? 'opacity-50' : ''}`}>
                                  {t(`settings.${role.toLowerCase()}`)}
                                  {!canAssignRole && ` (${t('settings.superadminOnly')})`}
                                </label>
                    </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {inviteError && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                          {inviteError}
                        </div>
                      )}
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => setInviteModalOpen(false)}
                          className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                        >
                          {t('general.cancel')}
                        </button>
                        <button
                          onClick={handleInviteUser}
                          disabled={inviteLoading}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                        >
                          {inviteLoading ? t('settings.inviting') : t('settings.invite')}
                        </button>
                    </div>
                    </div>
                  </div>
                )}
                
                {/* Edit User Modal */}
                {editUserModalOpen && selectedUser && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h3 className="text-xl font-semibold mb-4">
                        {t('settings.editUser')}: {selectedUser.name}
                      </h3>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.email')}
                        </label>
                        <input
                          type="email"
                          value={selectedUser.email}
                          disabled
                          className="w-full p-2 border border-gray-300 rounded bg-gray-50"
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.selectRoles')}
                        </label>
                        <div className="space-y-2">
                          {['Viewer', 'Editor', 'Admin', 'Superadmin'].map((role) => {
                            // Check if current user can assign this role
                            const isSuperadminRole = role === 'Superadmin';
                            const isAdminRole = role === 'Admin';
                            
                            // Update to use current_school specific roles
                            // IMPORTANT FIX: Always use school ID from context, not user.current_school
                            const currentSchoolId = school?.id || user?.current_school?.id;
                            const userRolesForCurrentSchool = user?.schools
                              ?.find(school => school.id === currentSchoolId)?.roles || [];
                            const currentUserIsSuperadmin = userRolesForCurrentSchool.includes('Superadmin');
                            
                            // Apply the same permission rules as for editing users in the table:
                            // 1. Superadmins can assign any role
                            // 2. Admins cannot assign Admin or Superadmin roles
                            const canAssignRole = currentUserIsSuperadmin ? true : !(isSuperadminRole || isAdminRole);
                            
                            return (
                              <div key={role} className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`edit-role-${role}`}
                                  checked={selectedRoles.includes(role)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedRoles([...selectedRoles, role]);
                                    } else {
                                      setSelectedRoles(selectedRoles.filter(r => r !== role));
                                    }
                                  }}
                                  disabled={!canAssignRole}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                                <label htmlFor={`edit-role-${role}`} className={`ml-2 block text-sm text-gray-900 ${!canAssignRole ? 'opacity-50' : ''}`}>
                                  {t(`settings.${role.toLowerCase()}`)}
                                  {!canAssignRole && ` (${t('settings.superadminOnly')})`}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-3">
                      <button
                          onClick={() => setEditUserModalOpen(false)}
                          className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                        >
                          {t('general.cancel')}
                        </button>
                        <button
                          onClick={handleUpdateUserRoles}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700"
                        >
                          {t('settings.updateRoles')}
                      </button>
                    </div>
                </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'menuTypes' && (
              <div>
                {/* Default Menu Type Selector */}
                <div className="mb-6 py-3 pt-1 bg-white rounded-lg shadow-sm w-full">
                  <h2 className="text-xl font-semibold mb-2">{t('settings.defaultMenuType')}</h2>
                  <p className="text-sm text-gray-600 mb-3">{t('settings.defaultMenuTypeExplanation')}</p>
                  
                  <div className="flex items-center justify-between">
                    <select
                      value={schoolInfo.defaultMenuTypeId || ''}
                      onChange={(e) => setSchoolInfo({
                        ...schoolInfo,
                        defaultMenuTypeId: e.target.value ? Number(e.target.value) : null
                      })}
                      className="flex-grow mr-4 border border-gray-300 rounded-md shadow-sm p-2"
                      disabled={menuTypes.length === 0}
                    >
                      {!schoolInfo.defaultMenuTypeId && (
                        <option value="">{t('settings.selectDefaultMenuType')}</option>
                      )}
                      {menuTypes.map(menuType => {
                        const { text } = getBestTranslation(menuType);
                        return (
                          <option key={menuType.id} value={menuType.id}>
                            {text}{menuType.is_visible === false ? ` (${t('settings.hidden')})` : ''}
                        </option>
                        );
                      })}
                    </select>
                    <button
                      onClick={async () => {
                        try {
                          setIsSaving(true);
                          // Get the school ID using the getSchoolId helper function
                          const schoolId = getSchoolId();
                          
                          // Use the React Query mutation instead of direct service call
                          const response = await updateSchoolSettingsMutation.mutateAsync({
                            settings: { default_menu_type_id: schoolInfo.defaultMenuTypeId },
                            schoolId
                          });
                          
                          // Update local schoolInfo state to reflect the change immediately
                          setSchoolInfo(prev => ({
                            ...prev,
                            defaultMenuTypeId: response.default_menu_type_id || schoolInfo.defaultMenuTypeId
                          }));
                          
                          toast.success(t('settings.defaultMenuTypeUpdated'));
                        } catch (error) {
                          console.error('Error updating default menu type:', error);
                          toast.error(t('settings.errorUpdatingDefaultMenuType'));
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={menuTypes.length === 0 || isSaving}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300"
                    >
                      {isSaving ? t('settings.saving') : t('settings.updateDefaultMenuType')}
                    </button>
                  </div>
                  
                  {menuTypes.length === 0 && (
                    <p className="mt-2 text-red-500">{t('settings.noMenuTypesAvailable')}</p>
                  )}
                </div>
                
                {/* Header titles in a flex row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                  <h2 className="text-xl font-semibold">{t('settings.manageMenuTypes')}</h2>
                  <h3 className="text-xl font-semibold">{t('settings.addNewMenuType')}</h3>
                </div>
                
                {/* Horizontal layout container */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Menu types list with courses - left side */}
                  <div className="space-y-4" style={{ minHeight: "800px" }}>
                    {menuTypes.map(menuType => {
                      // --- Start: New Translation Logic --- 
                      const translations = menuType.translations || []; // Assuming array format
                      let displayText = null;
                      let displayLangCode = null;
                      const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
                      
                      // 1. Try current UI language
                      const currentTrans = translations.find(t => t.code === currentLanguage && t.name?.trim());
                      if (currentTrans) {
                        displayText = currentTrans.name;
                        displayLangCode = currentLanguage;
                      } 
                      // 2. Try school's default language
                      else if (defaultLangCode && defaultLangCode !== currentLanguage) {
                        const defaultTrans = translations.find(t => t.code === defaultLangCode && t.name?.trim());
                        if (defaultTrans) {
                          displayText = defaultTrans.name;
                          displayLangCode = defaultLangCode;
                        }
                      } 
                      // 3. Try other selected languages
                      if (!displayText) {
                        for (const lang of selectedLanguages) {
                          if (lang.code !== currentLanguage && lang.code !== defaultLangCode) {
                            const selectedTrans = translations.find(t => t.code === lang.code && t.name?.trim());
                            if (selectedTrans) {
                              displayText = selectedTrans.name;
                              displayLangCode = lang.code;
                              break; 
                            }
                          }
                        }
                      }
                      // 4. Try any other available language
                      if (!displayText) {
                        for (const trans of translations) {
                          const isAlreadyChecked = trans.code === currentLanguage || 
                                                   trans.code === defaultLangCode || 
                                                   selectedLanguages.some(sl => sl.code === trans.code);
                          if (!isAlreadyChecked && trans.name?.trim()) {
                            displayText = trans.name;
                            displayLangCode = trans.code;
                            break;
                          }
                        }
                      }
                      // 5. Final fallback string
                      if (!displayText) {
                        displayText = menuType.name || t('settings.noTranslationAvailable', 'N/A');
                        displayLangCode = null; 
                      }
                      
                      const isFallback = displayLangCode !== null && displayLangCode !== currentLanguage;
                      const isHidden = menuType.is_visible === false;

                      // Determine styling (Handle hidden state and fallback)
                      let nameClass = 'mr-1'; // Base margin
                      if (isHidden) {
                        nameClass += ' text-gray-500 italic font-medium'; // Hidden style (similar to disabled)
                      } else if (isFallback) {
                        nameClass += ' font-normal italic'; // Fallback style
                      } else {
                        nameClass += ' font-medium'; // Default style
                      }
                      // --- End: New Translation Logic ---
                      
                      const isExpanded = expandedMenuType === menuType.id;
                      
                      return (
                        <div 
                          key={menuType.id} 
                          className={`${isHidden ? 'bg-gray-100' : 'bg-white'} rounded-lg shadow-sm overflow-hidden`}
                          ref={el => menuTypeRefs.current[menuType.id] = el}
                        >
                          <div className="flex justify-between items-center p-4">
                            <div className="flex-grow flex items-center mr-4"> {/* Added flex-grow, flex, items-center, mr-4 */}
                              <span className={nameClass}>
                                {displayText}
                                {/* Display language code in gray if it's a fallback */}
                                {isFallback && displayLangCode && (
                                  <span className="text-xs ml-1 text-gray-400">
                                    ({displayLangCode.toUpperCase()})
                                  </span>
                                )}
                              </span>
                              {isHidden && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-300 text-gray-700">
                                  {t('settings.hidden')}
                                </span>
                              )}
                              
                              {/* Original Language Indicator Labels */}
                              <div className="flex space-x-1 ml-3 flex-shrink-0"> {/* Added flex-shrink-0 */}
                                {selectedLanguages.map(lang => {
                                  const trans = translations.find(t => t.code === lang.code);
                                  const hasTranslation = trans && trans.name && trans.name.trim() !== '';
                                  const title = hasTranslation 
                                    ? t(`languageNames.${lang.code}`) + ' (' + t('settings.translated') + ')' 
                                    : t(`languageNames.${lang.code}`) + ' (' + t('settings.noTranslation') + ')';
                                  
                                  return (
                                    <span 
                                      key={lang.code}
                                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold
                                        ${hasTranslation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'}`}
                                      title={title}
                                    >
                                      {lang.code.toUpperCase()}
                                    </span>
                                  );
                                })}
                              </div>

                              {/* Show indicators for nutrients and tags when they're disabled */}
                              {/*menuType.show_nutrients === false && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                  {t('settings.noNutrients', 'No Nutrients')}
                                </span>
                              )}
                              
                              {menuType.show_tags === false && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  {t('settings.noTags', 'No Tags')}
                                </span>
                              )*/}
                              
                            </div>
                            <div className="flex items-center flex-shrink-0"> {/* Added flex-shrink-0 */}
                              <button
                                className="text-blue-500 hover:text-blue-700 mr-3"
                                onClick={() => openEditModal('menuType', menuType)}
                              >
                                {t('settings.edit')}
                              </button>
                              <button
                                className="text-red-500 hover:text-red-700 mr-4"
                                onClick={() => openDeleteModal('menuType', menuType)}
                              >
                                {t('settings.delete')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Store the current scroll position
                                  const scrollPosition = window.scrollY;
                                  
                                  if (isExpanded) {
                                    setExpandedMenuType(null);
                                  } else {
                                    setExpandedMenuType(menuType.id);
                                    // React Query hooks will automatically fetch data when expandedMenuType changes
                                  }
                                  
                                  // Restore the scroll position after state update
                                  setTimeout(() => {
                                    window.scrollTo({
                                      top: scrollPosition,
                                      behavior: 'instant'
                                    });
                                  }, 0);
                                }}
                                className="text-gray-500"
                              >
                                <svg 
                                  className={`w-5 h-5 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`} 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  viewBox="0 0 20 20" 
                                  fill="currentColor"
                                >
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="p-4 border-t">
                              <h4 className="font-medium mb-3">{t('settings.coursesForMenuType')}</h4>
                              
                              {isLoadingCourses ? (
                                <p className="text-gray-500">{t('settings.loadingCourses')}</p>
                              ) : (
                                <>
                                  {menuTypeCourses.length === 0 ? (
                                    <p className="text-gray-500 mb-4">{t('settings.noCoursesForMenuType')}</p>
                                  ) : (
                                    <ul className="mb-4 space-y-2">
                                      {menuTypeCourses.map((course, index) => (
                                        <li key={course.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                          <span>{course.name}</span>
                                          <div className="flex items-center">
                                            <div className="flex mr-3">
                                              <button
                                                onClick={() => handleReorderCourse(menuType.id, course.id, 'up')}
                                                disabled={index === 0 || isSaving}
                                                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed mr-1"
                                                title={t('settings.moveUp')}
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                              <button
                                                onClick={() => handleReorderCourse(menuType.id, course.id, 'down')}
                                                disabled={index === menuTypeCourses.length - 1 || isSaving}
                                                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={t('settings.moveDown')}
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            </div>
                                            <button
                                              onClick={() => handleRemoveCourseFromMenuType(menuType.id, course.id)}
                                              className="text-red-500 hover:text-red-700"
                                              title={t('settings.removeCourse')}
                                            >
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  
                                  <div className="mt-4">
                                    <h5 className="font-medium mb-2">{t('settings.addCourseToMenuType')}</h5>
                                    {availableCourses.length === 0 ? (
                                      <p className="text-gray-500">{t('settings.noAvailableCourses')}</p>
                                    ) : (
                                      <div className="flex">
                                        <select
                                          value={selectedAvailableCourse || ''}
                                          onChange={(e) => setSelectedAvailableCourse(e.target.value ? Number(e.target.value) : null)}
                                          className="flex-grow mr-2 border border-gray-300 rounded-md shadow-sm p-2"
                                        >
                                          <option value="">{t('settings.selectCourse')}</option>
                                          {availableCourses.map(course => (
                                            <option key={course.id} value={course.id}>
                                              {course.name}
                                            </option>
                                          ))}
                                        </select>
                                        <button
                                          onClick={() => handleAddCourseToMenuType(menuType.id)}
                                          disabled={!selectedAvailableCourse}
                                          className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded disabled:bg-gray-300"
                                        >
                                          {t('settings.add')}
                                        </button>
                </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {menuTypes.length === 0 && (
                      <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-md">
                        {t('settings.noMenuTypesAvailable')}
                      </div>
                    )}
                  </div>
                  
                  {/* Add new menu type form - right side */}
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <form onSubmit={handleMenuTypeSubmit} className="space-y-4">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-gray-700">{t('settings.translations')}</h4>
                        </div>
                        {selectedLanguages.length > 0 ? (
                          // Sort languages to show default language first, then others alphabetically
                          [...selectedLanguages]
                            .sort((a, b) => {
                              // Put default language at the top
                              if (a.id === schoolInfo.defaultLanguageId) return -1;
                              if (b.id === schoolInfo.defaultLanguageId) return 1;
                              // Otherwise sort alphabetically by name
                              return a.name.localeCompare(b.name);
                            })
                            .map((lang) => {
                              const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                              return (
                                <div key={lang.code} className="flex items-start">
                                  <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                                    {t(`languageNames.${lang.code}`)} ({lang.code})
                                    {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                                  </span>
                                  <div className="flex-grow relative">
                    <input
                      type="text"
                                      name={`translation-${lang.code}`}
                                      placeholder={isDefaultLanguage ? 
                                        t('settings.requiredTranslation') : 
                                        t('settings.enterTranslation')
                                      }
                                      className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                                        ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                                      required={isDefaultLanguage}
                                    />
                                    {isDefaultLanguage && (
                                      <span className="text-xs text-blue-600 mt-1 block">
                                        {t('settings.defaultLanguageField')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                        ) : (
                          <p className="text-sm text-gray-500">{t('settings.noTranslationsAvailable')}</p>
                        )}
                      </div>
                      
                      {/* Visibility settings */}
                      <div className="mt-6 border-t pt-4">
                        <h4 className="font-medium text-gray-700 mb-2">{t('settings.menuTypeVisibility')}</h4>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="new-menu-type-visibility"
                            checked={newMenuTypeIsVisible}
                            onChange={(e) => setNewMenuTypeIsVisible(e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="new-menu-type-visibility" className="ml-2 block text-sm text-gray-700">
                            {t('settings.showMenuTypePublicly')}
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('settings.visibilityExplanation')}
                        </p>
                      </div>
                      
                      {/* Nutrients and Tags settings */}
                      <div className="mt-6 border-t pt-4">
                        <h4 className="font-medium text-gray-700 mb-2">{t('settings.menuTypeOptions')}</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="new-menu-type-show-nutrients"
                              checked={newMenuTypeShowNutrients}
                              onChange={(e) => setNewMenuTypeShowNutrients(e.target.checked)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="new-menu-type-show-nutrients" className="ml-2 block text-sm text-gray-700">
                              {t('settings.showNutrientsForMenuType')}
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="new-menu-type-show-tags"
                              checked={newMenuTypeShowTags}
                              onChange={(e) => setNewMenuTypeShowTags(e.target.checked)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="new-menu-type-show-tags" className="ml-2 block text-sm text-gray-700">
                              {t('settings.showTagsForMenuType')}
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('settings.nutrientTagExplanation')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                      {t('settings.add')}
                    </button>
                      </div>
                  </form>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'courses' && (
              <div>
                {/* Header titles in a flex row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                  <h2 className="text-xl font-semibold">{t('settings.manageCourses')}</h2>
                  <h3 className="text-xl font-semibold">{t('settings.addNewCourse')}</h3>
                </div>
                
                {/* Horizontal layout container */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Courses list - left side */}
                  <div className="space-y-2">
                    {courses.map((course) => {
                      // --- Start: New Translation Logic --- 
                      const translations = course.translations || []; // Assuming array format
                      let displayText = null;
                      let displayLangCode = null;
                      const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
                      
                      // 1. Try current UI language
                      const currentTrans = translations.find(t => t.code === currentLanguage && t.name?.trim());
                      if (currentTrans) {
                        displayText = currentTrans.name;
                        displayLangCode = currentLanguage;
                      } 
                      // 2. Try school's default language
                      else if (defaultLangCode && defaultLangCode !== currentLanguage) {
                        const defaultTrans = translations.find(t => t.code === defaultLangCode && t.name?.trim());
                        if (defaultTrans) {
                          displayText = defaultTrans.name;
                          displayLangCode = defaultLangCode;
                        }
                      } 
                      // 3. Try other selected languages
                      if (!displayText) {
                        for (const lang of selectedLanguages) {
                          if (lang.code !== currentLanguage && lang.code !== defaultLangCode) {
                            const selectedTrans = translations.find(t => t.code === lang.code && t.name?.trim());
                            if (selectedTrans) {
                              displayText = selectedTrans.name;
                              displayLangCode = lang.code;
                              break; 
                            }
                          }
                        }
                      }
                      // 4. Try any other available language
                      if (!displayText) {
                        for (const trans of translations) {
                          const isAlreadyChecked = trans.code === currentLanguage || 
                                                   trans.code === defaultLangCode || 
                                                   selectedLanguages.some(sl => sl.code === trans.code);
                          if (!isAlreadyChecked && trans.name?.trim()) {
                            displayText = trans.name;
                            displayLangCode = trans.code;
                            break;
                          }
                        }
                      }
                      // 5. Final fallback string
                      if (!displayText) {
                        displayText = course.name || t('settings.noTranslationAvailable', 'N/A');
                        displayLangCode = null; 
                      }
                      
                      const isFallback = displayLangCode !== null && displayLangCode !== currentLanguage;
                      
                      // Determine styling (No explicit 'disabled' for courses, just fallback)
                      let nameClass = 'mr-3'; // Base margin
                      if (isFallback) {
                        nameClass += ' font-normal italic'; // Fallback style
                      } else {
                        nameClass += ' font-medium'; // Default style
                      }
                      // --- End: New Translation Logic ---
                      
                      return (
                        <div key={course.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                          <div className="flex items-center flex-grow mr-4"> {/* Added flex-grow, mr-4 */}
                            <span className={nameClass}>
                              {displayText}
                              {/* Display language code in gray if it's a fallback */}
                              {isFallback && displayLangCode && (
                                <span className="text-xs ml-1 text-gray-400">
                                  ({displayLangCode.toUpperCase()})
                                </span>
                              )}
                            </span>
                            
                            {/* Original Language Indicator Labels */}
                            <div className="flex space-x-1 ml-3 flex-shrink-0"> {/* Added flex-shrink-0 */}
                              {selectedLanguages.map(lang => {
                                // Check translation based on the array structure
                                const trans = translations.find(t => t.code === lang.code);
                                const hasTranslation = trans && trans.name && trans.name.trim() !== '';
                                const title = hasTranslation 
                                  ? t(`languageNames.${lang.code}`) + ' (' + t('settings.translated') + ')' 
                                  : t(`languageNames.${lang.code}`) + ' (' + t('settings.noTranslation') + ')';
                                
                                return (
                                  <span 
                                    key={lang.code}
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold
                                      ${hasTranslation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'}`}
                                    title={title}
                                  >
                                    {lang.code.toUpperCase()}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <button
                              className="text-blue-500 hover:text-blue-700 mr-3"
                              onClick={() => openEditModal('course', course)}
                            >
                              {t('settings.edit')}
                            </button>
                            <button
                              className="text-red-500 hover:text-red-700"
                              onClick={() => openDeleteModal('course', course)}
                            >
                              {t('settings.delete')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {courses.length === 0 && (
                      <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-md">
                        {t('settings.noCourses')}
                      </div>
                    )}
                  </div>
                  
                  {/* Add new course form - right side */}
                  <div className="bg-gray-50 p-4 rounded-md">
                    <form onSubmit={handleCourseSubmit} className="space-y-4">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-gray-700">{t('settings.translations')}</h4>
                        </div>
                        {selectedLanguages.length > 0 ? (
                          // Sort languages to show default language first, then others alphabetically
                          [...selectedLanguages]
                            .sort((a, b) => {
                              // Put default language at the top
                              if (a.id === schoolInfo.defaultLanguageId) return -1;
                              if (b.id === schoolInfo.defaultLanguageId) return 1;
                              // Otherwise sort alphabetically by name
                              return a.name.localeCompare(b.name);
                            })
                            .map((lang) => {
                              const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                              return (
                                <div key={lang.code} className="flex items-start">
                                  <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                                    {t(`languageNames.${lang.code}`)} ({lang.code})
                                    {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                                  </span>
                                  <div className="flex-grow relative">
                    <input
                      type="text"
                                      name={`translation-${lang.code}`}
                                      placeholder={isDefaultLanguage ? 
                                        t('settings.requiredTranslation') : 
                                        t('settings.enterTranslation')
                                      }
                                      className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                                        ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                                      required={isDefaultLanguage}
                                    />
                                    {isDefaultLanguage && (
                                      <span className="text-xs text-blue-600 mt-1 block">
                                        {t('settings.defaultLanguageField')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                        ) : (
                          <p className="text-sm text-gray-500">{t('settings.noTranslationsAvailable')}</p>
                        )}
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                      {t('settings.add')}
                    </button>
                      </div>
                  </form>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'languages' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">{t('settings.manageLanguages')}</h2>
                  <button
                    onClick={handleSaveSelectedLanguages}
                    disabled={isSavingLanguages}
                    className={`px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 ${
                      isSavingLanguages ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSavingLanguages ? t('settings.saving') : t('settings.saveChanges')}
                  </button>
                </div>
                
                <p className="mb-4 text-gray-700">{t('settings.languagesExplanation')}</p>
                
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-2">{t('settings.defaultLanguage')}</h3>
                  <p className="text-sm text-gray-600 mb-3">{t('settings.defaultLanguageExplanation')}</p>
                  
                  <select
                    value={
                      // If there's a default language ID and it's in the selected languages, use it
                      selectedLanguages.some(lang => lang.id === schoolInfo.defaultLanguageId) 
                        ? schoolInfo.defaultLanguageId 
                        // Otherwise if there are any languages, use the first one
                        : selectedLanguages.length > 0 
                          ? selectedLanguages[0].id
                          // Only empty if no languages are selected
                          : ''
                    }
                    onChange={handleDefaultLanguageChange}
                    className={`block w-full max-w-md border ${!schoolInfo.defaultLanguageId ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-md shadow-sm p-2`}
                    required
                  >
                    {selectedLanguages.length > 0 ? (
                      selectedLanguages.map(language => (
                      <option key={language.id} value={language.id}>
                        {t(`languageNames.${language.code}`)} ({language.code})
                      </option>
                      ))
                    ) : (
                      <option value="" disabled>{t('settings.selectDefaultLanguage')}</option>
                    )}
                  </select>
                  {!schoolInfo.defaultLanguageId && selectedLanguages.length > 0 && (
                    <p className="text-sm text-red-500 mt-1">
                      {t('settings.defaultLanguageRequired')}
                    </p>
                  )}
                </div>
                
                <h3 className="text-lg font-medium mb-2">{t('settings.availableLanguages')}</h3>
                <div className="space-y-3 mt-6">
                  {languages.map((language) => {
                    const isSelected = selectedLanguages.some(lang => lang.id === language.id);
                    // Determine if this is the only selected language
                    const isOnlySelectedLanguage = isSelected && selectedLanguages.length === 1;
                    
                    return (
                      <div key={language.id} className="flex items-center p-3 bg-gray-50 rounded-md">
                        <input
                          type="checkbox"
                          id={`lang-${language.id}`}
                          checked={isSelected}
                          onChange={() => handleLanguageCheckboxChange(language.id)}
                          disabled={isOnlySelectedLanguage}
                          className={`w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${isOnlySelectedLanguage ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <label htmlFor={`lang-${language.id}`} className={`ml-3 text-gray-700 font-medium ${isOnlySelectedLanguage ? 'opacity-60' : ''}`}>
                          {t(`languageNames.${language.code}`)} ({language.code})
                          {isOnlySelectedLanguage && (
                            <span className="ml-2 text-sm text-amber-600">
                              ({t('settings.requiredLanguage')})
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>
                
                {languages.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t('settings.noLanguagesAvailable')}
                  </div>
                )}
                
                <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        {t('settings.languageWarning')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'nutrients' && (
              <NutrientsTab 
                schoolId={schoolId} 
                selectedLanguages={selectedLanguages} 
                schoolInfo={schoolInfo} 
                updateSchoolSettingsMutation={updateSchoolSettingsMutation} // Pass down the mutation hook
              />
            )}
            
            {activeTab === 'schoolInfo' && (
              <div>
                <h2 className="text-xl font-semibold mb-2">{t('settings.schoolInformation')}</h2>
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-medium mb-4">{t('settings.basicInformation')}</h3>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      debouncedHandleSchoolInfoSubmit(e);
                    }} className="space-y-4">
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.schoolName')}</label>
                    <input
                      type="text"
                      name="name"
                      value={schoolInfo.name}
                      onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.subdomain')}</label>
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <div className="mr-2 text-gray-500 text-sm">
                              https://schoolmenus.xyz/
                            </div>
                    <input
                              type="text"
                              name="subdomain"
                              value={schoolInfo.subdomain || ''}
                      onChange={handleInputChange}
                              className={`w-full border ${
                                subdomainStatus && !subdomainStatus.available && schoolInfo.subdomain !== initialSubdomain
                                  ? 'border-red-500'
                                  : subdomainStatus && subdomainStatus.available
                                  ? 'border-green-500'
                                  : 'border-gray-300'
                              } rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500`}
                              placeholder="schoolname"
                              disabled={cooldownActive}
                            />
                  </div>
                          
                          {/* Status indicator for subdomain availability */}
                          <div className="mt-1">
                            {isCheckingSubdomain ? (
                              <span className="text-gray-500 text-sm">{t('settings.checkingAvailability')}</span>
                            ) : schoolInfo.subdomain !== initialSubdomain && subdomainStatus ? (
                              <span 
                                className={`text-sm ${
                                  subdomainStatus.available ? 'text-green-500' : 'text-red-500'
                                }`}
                              >
                                {subdomainStatus.message}
                              </span>
                            ) : null}
                    </div>
                          
                          {/* DNS propagation notice */}
                          {subdomainStatus && subdomainStatus.available && schoolInfo.subdomain !== initialSubdomain && (
                            <div className="mt-1 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                              <p>{t('dnsNoteHeading')}</p>
                              <ul className="list-disc ml-5 mt-1">
                                <li>{t('dnsNote1')}</li>
                                <li>{t('dnsNote2')}</li>
                                <li>{t('dnsNote3')}</li>
                              </ul>
                            </div>
                          )}
                          
                          {/* Cooldown notification */}
                          {cooldownInfo && (
                            <div className="mt-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                              <p className="font-semibold">{t('settings.subdomainCooldown')}</p>
                              <p>{t('settings.subdomainChangeMessage')}</p>
                              <p className="mt-1">
                                {t('settings.cooldownWillEndOn', { 
                                  date: formatDateDDMMYYYY(new Date(cooldownInfo.cooldownEnds)), 
                                  time: new Date(cooldownInfo.cooldownEnds).toLocaleTimeString() 
                                })}
                              </p>
                            </div>
                          )}
                    </div>
                  </div>
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.homepageUrl')}</label>
                    <input
                          type="url"
                          name="homepageUrl"
                          value={schoolInfo.homepageUrl || ''}
                      onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://www.yourschool.edu"
                    />
                  </div>
                  
                      <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        disabled={isSubmittingSchoolInfo}
                    >
                        {isSubmittingSchoolInfo ? t('common.saving') : t('settings.saveBasicInfo')}
                    </button>
                  </div>
                </form>
              </div>

              {/* --- Public Month Navigation Settings --- */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('monthNavigation.title')}</h3>
                <p className="text-sm text-gray-600 mb-4">
                    {schoolInfo.monthly_public_display
                      ? t('monthNavigation.monthlyDescription', 'The public menu will display a full month at a time.')
                      : t('monthNavigation.weeklyDescription', 'The public menu will display a week at a time.')}
                </p>


                <form onSubmit={(e) => {
                  e.preventDefault();
                  debouncedHandleSchoolInfoSubmit(e);
                }}>
                  {/* Display Type Toggle (Monthly/Weekly) */}
                  <div className="flex items-center mb-4">
                    <label htmlFor="monthly_public_display" className="block text-sm font-medium text-gray-700 mr-2">
                      {t('monthNavigation.displayType')}:
                      <span className="ml-2 text-sm font-normal text-gray-500">
                          {t('monthNavigation.weekly')}
                      </span>
                    </label>
                    <button
                      type="button"
                      className={`${
                        schoolInfo.monthly_public_display ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                      role="switch"
                      aria-checked={schoolInfo.monthly_public_display}
                      onClick={() => handleInputChange({ target: { name: 'monthly_public_display', type: 'checkbox', checked: !schoolInfo.monthly_public_display }})}
                      id="monthly_public_display"
                      name="monthly_public_display"
                    >
                      <span
                        aria-hidden="true"
                        className={`${
                          schoolInfo.monthly_public_display ? 'translate-x-5' : 'translate-x-0'
                        } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                      />
                    </button>
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      {t('monthNavigation.monthly')}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    {schoolInfo.monthly_public_display
                      ? t('monthNavigation.monthlyDescription')
                      : t('monthNavigation.weeklyDescription')
                    }
                  </p>
                  <p className="text-sm text-gray-600 mb-4">{t('monthNavigation.description')}</p>
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center mb-4">
                    <label htmlFor="enable_public_month_navigation" className="block text-sm font-medium text-gray-700 mr-4">
                      {t('monthNavigation.enable')}
                    </label>
                    <button
                      type="button"
                      className={`${
                        schoolInfo.enable_public_month_navigation ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                      role="switch"
                      aria-checked={schoolInfo.enable_public_month_navigation}
                      onClick={() => handleInputChange({ target: { name: 'enable_public_month_navigation', type: 'checkbox', checked: !schoolInfo.enable_public_month_navigation }})}
                      id="enable_public_month_navigation"
                      name="enable_public_month_navigation"
                    >
                      <span
                        aria-hidden="true"
                        className={`${
                          schoolInfo.enable_public_month_navigation ? 'translate-x-5' : 'translate-x-0'
                        } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                      />
                    </button>
                  </div>

                  {/* Conditional Limits */}
                  {schoolInfo.enable_public_month_navigation && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="months_back_limit" className="block text-sm font-medium text-gray-700">{t('monthNavigation.monthsBack')}</label>
                          <input
                            type="number"
                            name="months_back_limit"
                            id="months_back_limit"
                            value={schoolInfo.months_back_limit || 0}
                            onChange={handleInputChange}
                            min="0"
                            max="12"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            disabled={isSubmittingSchoolInfo}
                          />
                          <p className="text-xs text-gray-500 mt-1">{t('monthNavigation.maxMonthsRange', { max: 12 })}</p>
                        </div>
                        <div>
                          <label htmlFor="months_ahead_limit" className="block text-sm font-medium text-gray-700">{t('monthNavigation.monthsAhead')}</label>
                          <input
                            type="number"
                            name="months_ahead_limit"
                            id="months_ahead_limit"
                            value={schoolInfo.months_ahead_limit || 0}
                            onChange={handleInputChange}
                            min="0"
                            max="12"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            disabled={isSubmittingSchoolInfo}
                          />
                          <p className="text-xs text-gray-500 mt-1">{t('monthNavigation.maxMonthsRange', { max: 12 })}</p>
                        </div>
                        <div>
                          <label htmlFor="days_after_month_start_for_back_navigation" className="block text-sm font-medium text-gray-700">{t('monthNavigation.daysAfterStart')}</label>
                          <input
                            type="number"
                            name="days_after_month_start_for_back_navigation"
                            id="days_after_month_start_for_back_navigation"
                            value={schoolInfo.days_after_month_start_for_back_navigation || 0}
                            onChange={handleInputChange}
                            min="0"
                            max="31"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            disabled={isSubmittingSchoolInfo}
                          />
                          <p className="text-xs text-gray-500 mt-1">{t('monthNavigation.maxDaysRange', { max: 31 })}</p>
                        </div>
                        <div>
                          <label htmlFor="days_before_month_end_for_forward_navigation" className="block text-sm font-medium text-gray-700">{t('monthNavigation.daysBeforeEnd')}</label>
                          <input
                            type="number"
                            name="days_before_month_end_for_forward_navigation"
                            id="days_before_month_end_for_forward_navigation"
                            value={schoolInfo.days_before_month_end_for_forward_navigation || 0}
                            onChange={handleInputChange}
                            min="0" 
                            max="31"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            disabled={isSubmittingSchoolInfo}
                          />
                          <p className="text-xs text-gray-500 mt-1">{t('monthNavigation.maxDaysRange', { max: 31 })}</p>
                        </div>
                      </div>
                  )}
                  {/* Save Button for this section */}
                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      disabled={isSubmittingSchoolInfo}
                    >
                      {isSubmittingSchoolInfo ? t('common.saving') : t('monthNavigation.saveSettings')}
                    </button>
                  </div>
                </form>
              </div>

                  {/* Custom Content */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-medium mb-4">{t('settings.customContent')}</h3>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      debouncedHandleCustomContentSubmit(e);
                    }} className="space-y-4">
                      <div className="space-y-4">
                        {selectedLanguages.map(language => (
                          <div key={language.id} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t(`languageNames.${language.code}`)} ({language.code})
                            </label>
                            <SimpleEditor
                              className="w-full custom-content"
                              name={`customContent_${language.code}`}
                              value={schoolInfo.customContent?.[language.code] || ''}
                              onChange={handleCustomContentChange}
                              minHeight={150}
                              placeholder={t('settings.customContentPlaceholder')}
                            />
                          </div>
                        ))}
                        {selectedLanguages.length === 0 && (
                          <p className="text-gray-500">{t('settings.noLanguagesSelected')}</p>
                        )}
                      </div>
                      
                      {/* Include Custom Content in Images Toggle */}
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">{t('settings.includeCustomContentInImage')}</label>
                            <p className="text-sm text-gray-600 mt-1">
                              {t('settings.includeCustomContentInImageExplanation')}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="include_custom_content_in_image"
                              checked={schoolInfo.include_custom_content_in_image || false}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end pt-2">
                        <button
                          type="submit"
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                          disabled={isSubmittingSchoolInfo}
                        >
                          {isSubmittingSchoolInfo ? t('common.saving') : t('settings.saveCustomContent')}
                        </button>
        </div>
                    </form>
      </div>
      
                  {/* Disclaimer Text */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-medium mb-4">{t('settings.disclaimerText')}</h3>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      debouncedHandleDisclaimerTextSubmit(e);
                    }} className="space-y-4">
                      <div className="space-y-4">
                        {selectedLanguages.map(language => (
                          <div key={language.id} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t(`languageNames.${language.code}`)} ({language.code})
                            </label>
                            <textarea
                              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500"
                              name={`disclaimerText_${language.code}`}
                              value={schoolInfo.disclaimerText?.[language.code] || ''}
                              onChange={handleDisclaimerTextChange}
                              rows={3}
                              placeholder={t('settings.disclaimerTextPlaceholder')}
                            />
                          </div>
                        ))}
                        {selectedLanguages.length === 0 && (
                          <p className="text-gray-500">{t('settings.noLanguagesSelected')}</p>
                        )}
                      </div>
                      <div className="flex justify-end pt-2">
                        <button
                          type="submit"
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                          disabled={isSubmittingSchoolInfo}
                        >
                          {isSubmittingSchoolInfo ? t('common.saving') : t('settings.saveDisclaimerText')}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Branding */}
              <div className="bg-white p-6">
                    <h3 className="text-lg font-medium mb-4">{t('settings.branding')}</h3>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      debouncedHandleSchoolInfoSubmit(e);
                    }} className="space-y-4">
                      <div className="mt-4">
                        <ColorPicker
                          label={t('settings.primaryBgColor')}
                          value={schoolInfo.primaryBgColor}
                          onChange={(color) => setSchoolInfo(prev => ({ ...prev, primaryBgColor: color }))}
                          id="primaryBgColor"
                          name="primaryBgColor"
                          className="mb-4"
                        />
                        
                        <ColorPicker
                          label={t('settings.primaryTextColor')}
                          value={schoolInfo.primaryTextColor}
                          onChange={(color) => setSchoolInfo(prev => ({ ...prev, primaryTextColor: color }))}
                          id="primaryTextColor"
                          name="primaryTextColor"
                          className="mb-4"
                        />
                        
                        <ColorPicker
                          label={t('settings.secondaryBgColor')}
                          value={schoolInfo.secondaryBgColor}
                          onChange={(color) => setSchoolInfo(prev => ({ ...prev, secondaryBgColor: color }))}
                          id="secondaryBgColor"
                          name="secondaryBgColor"
                          className="mb-4"
                        />
                        
                        <ColorPicker
                          label={t('settings.secondaryTextColor')}
                          value={schoolInfo.secondaryTextColor}
                          onChange={(color) => setSchoolInfo(prev => ({ ...prev, secondaryTextColor: color }))}
                          id="secondaryTextColor"
                          name="secondaryTextColor"
                          className="mb-4"
              />
            </div>
            
                      <div className="flex flex-col space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.schoolLogo')}</label>
                        <FileUploader
                          accept="image/*"
                          acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.svg']}
                          maxSizeMB={2}
                          onFileSelected={handleLogoUpload}
                          defaultPreview={schoolInfo.logoPreview}
                          allowRemove={false}
                        />
                        <p className="text-xs text-gray-500">{t('settings.logoNote')}</p>
                      </div>
                      
                      <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                          disabled={isSubmittingSchoolInfo}
                        >
                          {isSubmittingSchoolInfo ? t('common.saving') : t('settings.saveBranding')}
                    </button>
                  </div>
                </form>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tags' && (
              <TagTab
                schoolId={getSchoolId()}
                selectedLanguages={selectedLanguages}
                schoolInfo={schoolInfo}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={closeModal} title={`${t('settings.edit')} ${editModalType === 'menuType' ? t('settings.menuType') : t('settings.course')}`}>
        {editModalItem && (
          <form onSubmit={handleEditSubmit} className="space-y-6">
            {/* Modal content - translations for both menu types and courses */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
              <h4 className="font-medium text-gray-700">{t('settings.translations')}</h4>
              </div>
              {Object.entries(currentTranslations).length > 0 ? (
                // Sort translations to show default language first, then others alphabetically
                Object.entries(currentTranslations)
                  .sort(([codeA], [codeB]) => {
                    const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code || 'en';
                    // Put default language at the top
                    if (codeA === defaultLangCode) return -1;
                    if (codeB === defaultLangCode) return 1;
                    // Otherwise sort alphabetically by language name
                    const langA = selectedLanguages.find(l => l.code === codeA);
                    const langB = selectedLanguages.find(l => l.code === codeB);
                    const nameA = langA ? t(`languageNames.${langA.code}`) : codeA;
                    const nameB = langB ? t(`languageNames.${langB.code}`) : codeB;
                    return nameA.localeCompare(nameB);
                  })
                  .map(([langCode, trans]) => {
                    // Determine if this is using a fallback from another language
                    const isFallback = !trans.isOriginal && trans.text;
                    // Find the language name from the selectedLanguages
                    const language = selectedLanguages.find(l => l.code === langCode);
                    // Get default language code - note the use of ? after finding the language
                    const defaultLangCode = languages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
                    const isDefaultLanguage = langCode === defaultLangCode;
                    
                    return (
                      <div key={langCode} className="flex items-start">
                        <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                          {language ? t(`languageNames.${language.code}`) : langCode.toUpperCase()}
                          {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className="flex-grow relative">
                    <input
                      type="text"
                            value={trans.text}
                            onChange={(e) => handleTranslationChange(langCode, e.target.value)}
                            className={`w-full border rounded-md shadow-sm p-2 ${
                              isDefaultLanguage 
                                ? 'border-blue-300 bg-blue-50' 
                                : isFallback 
                                  ? 'text-gray-500 italic border-gray-300 bg-gray-50' 
                                  : 'border-gray-300'
                            }`}
                            required={isDefaultLanguage}
                            placeholder={isDefaultLanguage ? t('settings.requiredTranslation') : t('settings.enterTranslation')}
                          />
                          {isDefaultLanguage && (
                            <span className="text-xs text-blue-600 mt-1 block">
                              {t('settings.defaultLanguageField')}
                            </span>
                          )}
                          {isFallback && (
                            <div className="text-xs text-gray-400 mt-1">
                              {t('settings.usingFallbackTranslation')}
                  </div>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm text-gray-500">{t('settings.noTranslationsAvailable')}</p>
              )}
            </div>
            
            {/* Visibility settings - only shown for menu types */}
            {editModalType === 'menuType' && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">{t('settings.menuTypeVisibility')}</h4>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit-menu-type-visibility"
                    checked={menuTypeVisibility}
                    onChange={(e) => setMenuTypeVisibility(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="edit-menu-type-visibility" className="ml-2 block text-sm text-gray-700">
                    {t('settings.showMenuTypePublicly')}
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.visibilityExplanation')}
                </p>
              </div>
            )}
            
            {/* Nutrients and Tags settings - only shown for menu types */}
            {editModalType === 'menuType' && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">{t('settings.menuTypeOptions')}</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit-menu-type-show-nutrients"
                      checked={menuTypeShowNutrients}
                      onChange={(e) => setMenuTypeShowNutrients(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="edit-menu-type-show-nutrients" className="ml-2 block text-sm text-gray-700">
                      {t('settings.showNutrientsForMenuType')}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit-menu-type-show-tags"
                      checked={menuTypeShowTags}
                      onChange={(e) => setMenuTypeShowTags(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="edit-menu-type-show-tags" className="ml-2 block text-sm text-gray-700">
                      {t('settings.showTagsForMenuType')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.nutrientTagExplanation')}
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                {t('settings.cancel')}
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('settings.save')}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={deleteModalOpen} 
        onClose={closeDeleteModal} 
        title={
          deleteModalType === 'menuType' 
            ? t('settings.deleteMenuTypeConfirmation') 
            : t('settings.deleteCourseConfirmation')
        }
      >
        <div className="space-y-4">
          {deleteError ? (
            // Show error message if item cannot be deleted
            <div className="text-red-600 p-4 bg-red-50 rounded">
              <p className="font-semibold">
                {deleteError.type === 'menuType' ? (
                  // For menu types - use manual interpolation
                  <>
                    {interpolate(
                      t('settings.cannotDeleteMenuTypeInUse'), 
                      { count: deleteError.count }
                    )}
                    <br />
                  </>
                ) : (
                  // For courses - use manual interpolation
                  <>
                    {interpolate(
                      t('settings.cannotDeleteCourseInUse'), 
                      { count: deleteError.count }
                    )}
                    <br />
                  </>
                )}
              </p>
              <p className="mt-2">
                {deleteModalType === 'menuType' 
                  ? t('settings.menuTypeInUseExplanation') 
                  : t('settings.courseInUseExplanation')
                }
              </p>
            </div>
          ) : (
            // Show confirmation message if item can be deleted
            <p>
              {deleteModalType === 'menuType' ? (
                // For menu types - use manual interpolation
                interpolate(
                  t('settings.confirmDeleteMenuType'), 
                  { name: deleteModalItem?.name || '' }
                )
              ) : (
                // For courses - use manual interpolation
                interpolate(
                  t('settings.confirmDeleteCourse'), 
                  { name: deleteModalItem ? 
                    (typeof getBestTranslation === 'function' ? 
                      getBestTranslation(deleteModalItem)?.text || '' : 
                      deleteModalItem?.name || '') : 
                    '' 
                  }
                )
              )}
            </p>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={closeDeleteModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              {t('settings.cancel')}
            </button>
            
            {!deleteError && (
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {t('settings.confirmDelete')}
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* User removal confirmation modal */}
      <ConfirmationModal
        isOpen={deleteUserModalOpen}
        onClose={cancelRemoveUser}
        onConfirm={confirmRemoveUser}
        title={t('settings.confirmRemoveUserTitle')}
        message={userToDelete ? t('settings.confirmRemoveUserMessage', { email: userToDelete.email }) : ''}
        confirmText={t('settings.remove')}
        cancelText={t('settings.cancel')}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

export default SettingsPage; 